import { useState, useEffect, useRef, useCallback } from 'react';
import { useVisitorData } from '../../contexts/VisitorContext';
import axios from 'axios';
import api, { visitorAPI } from '../../services/api';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useSettings } from '../../contexts/SettingsContext';
import { getImageUrl } from '../../services/api';
import { calculateDuration as formatMySQLDateTime, formatMySQLDate } from '../../utils/dateUtils';
import PageTitle from '../shared/PageTitle';
import { getCloseIconUrl } from '../../utils/closeIconUrl';
// import { useNavigate } from 'react-router-dom';

// Initial form state
const initialFormState = {
  visitor_name: '',
  unit_id: '',
  purpose: '',
  phone: '',
  expected_duration: '1',
  photo: null,
  id_document: null,
  original_photo_url: null,      // Store original photo URL from previous visitor
  original_id_document_url: null, // Store original ID document URL from previous visitor
  requires_approval: false  // Explicitly set to false by default
};

// Add utility functions at the top before they're used
const processVisitorData = (visitor) => {
  const currentYear = new Date().getFullYear();

  // Sanitize/fix timestamps before returning
  let timeIn = visitor.time_in;
  let timeOut = visitor.time_out;

  // Fix times using manual parsing if they're in the MySQL format
  if (timeIn && typeof timeIn === 'string' && timeIn.includes(' ') && !timeIn.includes('T')) {
    try {
      // Extract date parts
      const parts = timeIn.split(/[- :]/);
      if (parts.length >= 6) {
        const year = parseInt(parts[0], 10);
        const month = parseInt(parts[1], 10) - 1; // JS months are 0-based
        const day = parseInt(parts[2], 10);
        const hour = parseInt(parts[3], 10);
        const minute = parseInt(parts[4], 10);
        const second = parseInt(parts[5], 10);

        // If year is too far in future, use current year
        const correctedYear = (year > currentYear + 1) ? currentYear : year;

        // Create a date with proper components
        const correctedDate = new Date(correctedYear, month, day, hour, minute, second);

        // Format back to MySQL format
        timeIn = correctedDate.toISOString().replace('T', ' ').substring(0, 19);
      }
    } catch (err) {
      console.warn('Error fixing time_in:', err);
    }
  }

  // Same for timeOut
  if (timeOut && typeof timeOut === 'string' && timeOut.includes(' ') && !timeOut.includes('T')) {
    try {
      // Extract date parts
      const parts = timeOut.split(/[- :]/);
      if (parts.length >= 6) {
        const year = parseInt(parts[0], 10);
        const month = parseInt(parts[1], 10) - 1; // JS months are 0-based
        const day = parseInt(parts[2], 10);
        const hour = parseInt(parts[3], 10);
        const minute = parseInt(parts[4], 10);
        const second = parseInt(parts[5], 10);

        // If year is too far in future, use current year
        const correctedYear = (year > currentYear + 1) ? currentYear : year;

        // Create a date with proper components
        const correctedDate = new Date(correctedYear, month, day, hour, minute, second);

        // Format back to MySQL format
        timeOut = correctedDate.toISOString().replace('T', ' ').substring(0, 19);
      }
    } catch (err) {
      console.warn('Error fixing time_out:', err);
    }
  }

  return {
    ...visitor,
    // Ensure required fields exist
    id: visitor.id || null,
    visitor_name: visitor.visitor_name || '',
    status: visitor.status || 'pending',
    time_in: timeIn || null,
    time_out: timeOut || null,
    unit_id: visitor.unit_id || null,
    villa_number: visitor.villa_number || '',
    purpose: visitor.purpose || '',
    phone: visitor.phone || '',
    expected_date: visitor.expected_date || null,
    expected_time: visitor.expected_time || null,
    created_at: visitor.created_at || null
  };
};

const processUnitData = (unit) => {
  return {
    ...unit,
    id: unit.id || null,
    villa_number: unit.villa_number || '',
    organization_id: unit.organization_id || null
  };
};

// Helper functions for localStorage
const saveImageToLocalStorage = (visitorId, imageType, imageData) => {
  try {
    if (!visitorId || !imageData) return false;
    const key = `visitor_${imageType}_${visitorId}`;
    localStorage.setItem(key, imageData);
    return true;
  } catch (error) {
    console.error(`Error saving ${imageType} to localStorage:`, error);
    return false;
  }
};

const getImageFromLocalStorage = (visitorId, imageType) => {
  try {
    if (!visitorId) return null;
    const key = `visitor_${imageType}_${visitorId}`;
    return localStorage.getItem(key);
  } catch (error) {
    console.error(`Error getting ${imageType} from localStorage:`, error);
    return null;
  }
};

const saveVisitorAsActive = (visitorId) => {
  try {
    localStorage.setItem(`visitor_status_${visitorId}`, 'active');
    console.log(`Saved visitor ${visitorId} as active in localStorage`);
    return true;
  } catch (err) {
    console.error(`Failed to save visitor ${visitorId} as active:`, err);
    return false;
  }
};

const removeVisitorActiveStatus = (visitorId) => {
  try {
    localStorage.removeItem(`visitor_status_${visitorId}`);
    localStorage.removeItem(`forceActiveVisitor_${visitorId}`);
    console.log(`Removed active status for visitor ${visitorId} from localStorage`);
    return true;
  } catch (err) {
    console.error(`Failed to remove active status for visitor ${visitorId}:`, err);
    return false;
  }
};

// Helper function to convert data URL to File object
const dataURLToFile = (dataURL, filename) => {
  if (!dataURL) return null;

  try {
    // Extract the MIME type and base64 data
    const arr = dataURL.split(',');
    if (arr.length < 2) {
      console.error('Invalid data URL format');
      return null;
    }

    // Get the mime type
    let mime = 'image/jpeg'; // Default
    if (arr[0].match(/:(.*?);/)) {
      mime = arr[0].match(/:(.*?);/)[1];
    }

    // Get file extension from mime type
    let extension = 'jpg';
    if (mime === 'image/png') extension = 'png';
    if (mime === 'image/gif') extension = 'gif';

    // Add extension to filename if not already there
    if (!filename.includes('.')) {
      filename = `${filename}.${extension}`;
    }

    // Convert base64 to binary
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);

    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }

    // Create and return file object
    const file = new File([u8arr], filename, { type: mime });
    console.log(`Created file from data URL: ${filename}, size: ${file.size} bytes, type: ${mime}`);
    return file;
  } catch (error) {
    console.error('Error converting data URL to file:', error);
    return null;
  }
};

// Function to directly update images in the server database
const saveImagesDirectlyToServer = async (visitorId, photoData, idDocData) => {
  if (!visitorId) {
    console.error('No visitor ID provided for direct image update');
    return false;
  }

  try {
    const payload = {};

    if (photoData) {
      // Ensure photo is in proper data URL format
      if (typeof photoData === 'string') {
        if (photoData.startsWith('data:image')) {
          payload.photo = photoData;
        } else {
          payload.photo = `data:image/jpeg;base64,${photoData}`;
        }
        console.log(`Prepared photo data for visitor ${visitorId}, data length: ${payload.photo?.length || 0}`);
      }
    }

    if (idDocData) {
      // Ensure ID document is in proper data URL format
      if (typeof idDocData === 'string') {
        if (idDocData.startsWith('data:image')) {
          payload.id_document = idDocData;
        } else {
          payload.id_document = `data:image/jpeg;base64,${idDocData}`;
        }
        console.log(`Prepared ID document data for visitor ${visitorId}, data length: ${payload.id_document?.length || 0}`);
      }
    }

    if (Object.keys(payload).length === 0) {
      console.warn('No valid image data to update');
      return false;
    }

    console.log(`Sending image update for visitor ${visitorId} to server...`);

    // Get authentication token
    const token = localStorage.getItem('token');
    if (!token) {
      console.error('Auth token missing, cannot update images');
      return false;
    }

    // Common request headers
    // const headers = {
    //   'Authorization': `Bearer ${localStorage.getItem('token')}`,
    //   'Content-Type': 'application/json'
    // };

    // Force localhost in development, ignore environment variable if it points to production
    const baseUrl = (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
      ? 'http://1'
      : (process.env.REACT_APP_API_URL || 'https://nivaas.app');

    let uploadSuccess = false;
    let photoUrl = null;
    let idDocUrl = null;

    // Try multiple upload methods in order until one succeeds

    // Method 1: Use the dedicated images endpoint
    try {
      console.log('Method 1: Using dedicated image update endpoint');
      const response = await axios.patch(
        `${baseUrl}/api/visitors/${visitorId}/images`,
        payload,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data && response.data.success) {
        console.log(`Successfully updated images for visitor ${visitorId} using Method 1`);
        uploadSuccess = true;
        photoUrl = response.data.photoUrl;
        idDocUrl = response.data.idDocUrl;
      } else {
        console.warn('Method 1 returned non-success response:', response.data);
      }
    } catch (error) {
      console.error('Method 1 failed:', error);
    }

    // If first method failed, try updating the entire visitor record
    if (!uploadSuccess) {
      try {
        console.log('Method 2: Trying direct visitor update endpoint');
        const response2 = await axios.put(
          `${baseUrl}/api/visitors/${visitorId}`,
          payload,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );

        if (response2.data && response2.data.success) {
          console.log('Method 2 successful');
          uploadSuccess = true;
          photoUrl = response2.data.photo_url;
          idDocUrl = response2.data.id_document_url;
        } else {
          console.warn('Method 2 returned non-success response:', response2.data);
        }
      } catch (error) {
        console.error('Method 2 failed:', error);
      }
    }

    // As a last resort, try creating a FormData object and using the upload-images endpoint
    if (!uploadSuccess) {
      try {
        console.log('Method 3: Trying FormData with upload-images endpoint');

        // Create files from data URLs
        const formData = new FormData();

        if (photoData) {
          const photoFile = dataURLToFile(photoData, `visitor_photo_${visitorId}_${Date.now()}.jpg`);
          if (photoFile) {
            formData.append('photo', photoFile);
          }
        }

        if (idDocData) {
          const idFile = dataURLToFile(idDocData, `visitor_id_${visitorId}_${Date.now()}.jpg`);
          if (idFile) {
            formData.append('id_document', idFile);
          }
        }

        const response3 = await axios.post(
          `${baseUrl}/api/visitors/${visitorId}/upload-images`,
          formData,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'multipart/form-data'
            }
          }
        );

        if (response3.data && response3.data.success) {
          console.log('Method 3 successful');
          uploadSuccess = true;
          photoUrl = response3.data.photoUrl;
          idDocUrl = response3.data.idDocUrl;
        } else {
          console.warn('Method 3 returned non-success response:', response3.data);
        }
      } catch (error) {
        console.error('Method 3 failed:', error);
      }
    }

    // Generate URLs if we didn't get them from the server responses
    if (uploadSuccess && (!photoUrl || !idDocUrl)) {
      const user = JSON.parse(sessionStorage.getItem('user') || '{}');
      const orgId = user.organization_id || 5;
      const timestamp = Date.now();

      if (photoData && !photoUrl) {
        photoUrl = `/api/visitors/photo/visitor_${visitorId}_org_${orgId}_${timestamp}.jpg`;
      }

      if (idDocData && !idDocUrl) {
        idDocUrl = `/api/visitors/id-document/id_doc_${visitorId}_org_${orgId}_${timestamp}.jpg`;
      }
    }

    return {
      success: uploadSuccess,
      photoUrl: photoUrl,
      idDocUrl: idDocUrl
    };
  } catch (error) {
    console.error('Error in direct image update:', error);
    return false;
  }
};

// Create a dedicated function to handle image uploads with better error handling
const uploadVisitorImages = async (visitorId, photoFile, idDocFile) => {
  console.log(`Starting dedicated image upload for visitor ID: ${visitorId}`);

  if (!visitorId) {
    console.log('No visitor ID provided for image upload');
    return false;
  }

  if (!photoFile && !idDocFile) {
    console.log('No images to upload for visitor ID:', visitorId);
    return true; // Not an error if no images were provided
  }

  const token = localStorage.getItem('token');
  if (!token) {
    console.log('No authentication token available for image upload');
    return false;
  }

  // Force localhost in development, ignore environment variable if it points to production
  const baseUrl = (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
    ? 'https://nivaas.app'
    : (process.env.REACT_APP_API_URL || 'https://nivaas.app');

  try {
    // Create FormData object for file upload
    const formData = new FormData();

    if (photoFile) {
      console.log(`Adding photo file to upload for visitor ID: ${visitorId}`);
      formData.append('photo', photoFile);
    }

    if (idDocFile) {
      console.log(`Adding ID document file to upload for visitor ID: ${visitorId}`);
      formData.append('id_document', idDocFile);
    }

    formData.append('visitorId', visitorId);

    let uploadSuccess = false;
    let photoUrl = null;
    let idDocUrl = null;

    // Try multiple upload methods, starting with the dedicated endpoint
    const attempts = [
      // Method 1: Dedicated image upload endpoint if it exists
      {
        method: 'post',
        url: `${baseUrl}/api/visitors/${visitorId}/images`,
        headers: {
          'Authorization': `Bearer ${token}`,
          // Let axios set the correct Content-Type for FormData
        },
        data: formData
      },

      // Method 2: Updating the visitor record with the image files
      {
        method: 'put',
        url: `${baseUrl}/api/visitors/${visitorId}`,
        headers: {
          'Authorization': `Bearer ${token}`,
          // Let axios set the correct Content-Type for FormData
        },
        data: formData
      }
    ];

    // Try each upload method until one succeeds
    for (const attempt of attempts) {
      try {
        console.log(`Trying image upload with method: ${attempt.method.toUpperCase()} ${attempt.url}`);
        const response = await axios(attempt);

        console.log(`Image upload response (${attempt.method}):`, response.status, response.data);

        if (response.status === 200 || response.status === 201) {
          console.log('Image upload successful!');

          // Try to extract the image URLs from the response
          if (response.data) {
            if (response.data.photo_url) {
              photoUrl = response.data.photo_url;
            }
            if (response.data.id_document_url) {
              idDocUrl = response.data.id_document_url;
            }
          }

          uploadSuccess = true;
          break;
        }
      } catch (error) {
        console.log(`Image upload attempt (${attempt.method}) failed:`, error.message);
      }
    }

    // If none of the upload attempts worked, try direct database update
    if (!uploadSuccess) {
      console.log('All upload attempts failed. Trying direct URL update...');

      // Generate standard URLs based on visitor ID
      const user = JSON.parse(sessionStorage.getItem('user') || '{}');
      const orgId = user.organization_id || 5;
      const timestamp = Date.now();

      if (photoFile) {
        photoUrl = `/api/visitors/photo/visitor_${visitorId}_org_${orgId}_${timestamp}.jpg`;
      }

      if (idDocFile) {
        idDocUrl = `/api/visitors/id-document/id_doc_${visitorId}_org_${orgId}_${timestamp}.jpg`;
      }

      // Update the database directly with the URLs
      try {
        console.log('Updating database with generated URLs:', { photoUrl, idDocUrl });
        const updateData = {};

        if (photoUrl) {
          updateData.photo_url = photoUrl;
        }

        if (idDocUrl) {
          updateData.id_document_url = idDocUrl;
        }

        const updateResponse = await axios({
          method: 'put',
          url: `${baseUrl}/api/visitors/${visitorId}`,
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          data: updateData
        });

        console.log('Direct URL update response:', updateResponse.status, updateResponse.data);

        if (updateResponse.status === 200) {
          uploadSuccess = true;
        }
      } catch (updateError) {
        console.log('Direct URL update failed:', updateError.message);
      }
    }

    // If we have successfully updated the URLs, store the actual image data in localStorage as backup
    if (uploadSuccess) {
      console.log('Storing image data in localStorage as backup');

      try {
        if (photoFile) {
          // Convert file to data URL for localStorage
          const photoDataUrl = await fileToDataURL(photoFile);
          saveImageToLocalStorage(visitorId, 'photo', photoDataUrl);
        }

        if (idDocFile) {
          // Convert file to data URL for localStorage
          const idDocDataUrl = await fileToDataURL(idDocFile);
          saveImageToLocalStorage(visitorId, 'id_document', idDocDataUrl);
        }
      } catch (storageError) {
        console.log('Failed to store images in localStorage:', storageError.message);
      }

      return { success: true, photoUrl, idDocUrl };
    }

    return false;

  } catch (error) {
    console.log('Image upload failed with error:', error.message);
    return false;
  }
};

// Helper function to convert File to dataURL
const fileToDataURL = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

// Add polyfill for older browsers at the top of the component
const Visitors = () => {
  // Refs for file inputs and video/canvas elements
  const fileInputRef = useRef(null);
  const idFileInputRef = useRef(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const idVideoRef = useRef(null);
  const idCanvasRef = useRef(null);

  // Add checkDataLoaded utility function inside component
  const checkDataLoaded = (villas) => {
    try {
      // Check if we have the essential data loaded
      const user = JSON.parse(sessionStorage.getItem('user') || '{}');
      const token = localStorage.getItem('token');
      const organizationId = user.organization_id;

      if (!token) {
        console.error('Authentication token not found');
        toast.error('Please log in again');
        return false;
      }

      if (!organizationId) {
        console.error('Organization context not found');
        toast.error('Organization context not found. Please log in again');
        return false;
      }

      // Check if we have loaded the initial data
      // Safely check for valid villas array
      const hasVillas = Array.isArray(villas) && villas.length > 0;
      if (!hasVillas) {
        console.error('Units data not loaded');
        toast.error('Please wait for data to load completely');
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error checking data loaded state:', error);
      toast.error('Error verifying data state. Please refresh the page');
      return false;
    }
  };

  // State variables for camera functionality
  const [cameraStream, setCameraStream] = useState(null);
  const [cameraLoading, setCameraLoading] = useState(false);
  const [cameraError, setCameraError] = useState(null);
  const [showFileUploadFallback, setShowFileUploadFallback] = useState(false);

  const [idCameraStream, setIdCameraStream] = useState(null);
  const [idCameraLoading, setIdCameraLoading] = useState(false);
  const [idCameraError, setIdCameraError] = useState(null);
  const [showIdFileUploadFallback, setShowIdFileUploadFallback] = useState(false);

  // Keep only essential initialization log
  // console.log("Visitors component initializing");

  // localStorage key for active tab
  const ACTIVE_TAB_STORAGE_KEY = 'guard_visitors_active_tab';

  // Visitor data
  const [loading, setLoading] = useState(false);
  const {
    visitors,
    villas,
    // addVisitor, 
    // checkoutVisitor, 
    refreshVisitors,
    loading: dataLoading,
    error: dataError,
    connectionError,
    checkInVisitor
  } = useVisitorData();
  // console.log('Guard component received visitors from context:', visitors.length);

  const [localVisitors, setLocalVisitors] = useState([]);
  const [localVillas, setLocalVillas] = useState([]);
  const [requestedVisitors, setRequestedVisitors] = useState([]);
  const [requestedVisitorsPagination, setRequestedVisitorsPagination] = useState({
    currentPage: 1,
    totalPages: 0,
    total: 0,
    limit: 20 // Static limit matching backend
  });
  
  // Daily visitors state
  const [dailyVisitors, setDailyVisitors] = useState([]);
  const [dailyVisitorsPagination, setDailyVisitorsPagination] = useState({
    currentPage: 1,
    totalPages: 0,
    total: 0,
    limit: 10
  });
  const [dailyVisitorsSearch, setDailyVisitorsSearch] = useState('');
  const [dailyVisitorsLoading, setDailyVisitorsLoading] = useState(false);
  const [showDailyVisitorModal, setShowDailyVisitorModal] = useState(false);
  const [dailyVisitorModalMode, setDailyVisitorModalMode] = useState('add'); // 'add' or 'edit'
  const [editingDailyVisitor, setEditingDailyVisitor] = useState(null);
  const [dailyVisitorFormData, setDailyVisitorFormData] = useState({
    name: '',
    contact: '',
    type: '',
    unit_ids: [], // multiple unit/villa IDs
    photo: null,
    id_document: null
  });
  const [dailyVisitorCapturedPhoto, setDailyVisitorCapturedPhoto] = useState(null);
  const [dailyVisitorCapturedIdDoc, setDailyVisitorCapturedIdDoc] = useState(null);
  const [dailyVisitorPhotoFile, setDailyVisitorPhotoFile] = useState(null);
  const [dailyVisitorIdDocFile, setDailyVisitorIdDocFile] = useState(null);
  const [dailyVisitorPhotoRemoved, setDailyVisitorPhotoRemoved] = useState(false); // Track if photo was explicitly removed
  const [dailyVisitorIdDocRemoved, setDailyVisitorIdDocRemoved] = useState(false); // Track if ID doc was explicitly removed
  const [showDailyVisitorCamera, setShowDailyVisitorCamera] = useState(false);
  const [showDailyVisitorIdCamera, setShowDailyVisitorIdCamera] = useState(false);
  const dailyVisitorVideoRef = useRef(null);
  const dailyVisitorCanvasRef = useRef(null);
  const dailyVisitorIdVideoRef = useRef(null);
  const dailyVisitorIdCanvasRef = useRef(null);
  const [dailyVisitorCameraStream, setDailyVisitorCameraStream] = useState(null);
  const [dailyVisitorIdCameraStream, setDailyVisitorIdCameraStream] = useState(null);
  
  const [error, setError] = useState(null);
  const [unitsLoaded, setUnitsLoaded] = useState(false);

  // Initialize activeTab from localStorage or default to 'check-in'
  const [activeTab, setActiveTab] = useState(() => {
    // Try to get saved tab from localStorage
    const savedTab = localStorage.getItem(ACTIVE_TAB_STORAGE_KEY);
    return savedTab || 'check-in';
  });

  // Watch for activeTab changes and save to localStorage
  useEffect(() => {
    localStorage.setItem(ACTIVE_TAB_STORAGE_KEY, activeTab);
  }, [activeTab]);

  // For visitor form
  const [formData, setFormData] = useState({
    visitor_name: '',
    unit_id: '',
    purpose: '',
    phone: '',
    expected_duration: '1',
    photo: null,
    id_document: null
  });

  // For success message
  const [showSuccess, setShowSuccess] = useState(false);
  const [checkedInVisitor, setCheckedInVisitor] = useState(null);

  // For checkout form
  const [checkoutId, setCheckoutId] = useState('');

  // For camera capture
  const [showCamera, setShowCamera] = useState(false);
  const [showIdCamera, setShowIdCamera] = useState(false);
  const [capturedImage, setCapturedImage] = useState(null);
  const [capturedIdImage, setCapturedIdImage] = useState(null);

  // Add state for visitor details modal
  const [selectedVisitor, setSelectedVisitor] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  // Add state for check-in modal
  const [showCheckInModal, setShowCheckInModal] = useState(false);
  const [visitorToCheckIn, setVisitorToCheckIn] = useState(null);

  // Import settings context
  const { settings } = useSettings();
  const primaryColor = settings.theme_primary_color || '#0d6efd';
  const closeIconColor = settings.block_title_color || '#8B4513';
  const closeIconUrl = getCloseIconUrl(closeIconColor);

  // For subscription plan limitations
  // const [planLimitations, setPlanLimitations] = useState({
  //   photoFeatureDisabled: false
  // });



  // Add these state variables near the top of the component
  const [directVisitorImageSrc, setDirectVisitorImageSrc] = useState('');
  const [directIdDocumentImageSrc, setDirectIdDocumentImageSrc] = useState('');
  const visitorImageRef = useRef(null);
  const idDocumentImageRef = useRef(null);

  // Create local placeholder image using data URL
  const createLocalPlaceholder = (text = 'No Image', width = 200, height = 200) => {
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');

    // Background
    ctx.fillStyle = '#f8f9fa';
    ctx.fillRect(0, 0, width, height);

    // Border
    ctx.strokeStyle = '#dee2e6';
    ctx.lineWidth = 2;
    ctx.strokeRect(1, 1, width - 2, height - 2);

    // Text
    ctx.fillStyle = '#6c757d';
    ctx.font = '16px Arial, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(text, width / 2, height / 2);

    return canvas.toDataURL();
  };

  // Add this function to directly set image sources via DOM manipulation
  // const setImageDirectly = (imgRef, src) => {
  //   if (imgRef && imgRef.current) {
  //     imgRef.current.src = src || createLocalPlaceholder('No Image');
  //   }
  // };

  // Function to start camera - MOVED ABOVE useEffect
  const startCamera = useCallback(async () => {
    // console.log("Starting camera...");
    setCameraLoading(true);
    setCameraError(null);

    try {
      // Simplify constraints to increase compatibility
      const constraints = {
        video: true,
        audio: false
      };

      // console.log("Requesting camera access with constraints:", constraints);
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      // console.log("Camera access granted, stream:", stream);
      setCameraStream(stream);

      if (videoRef.current) {
        // console.log("Setting video source to stream");
        videoRef.current.srcObject = stream;
        videoRef.current.onloadedmetadata = () => {
          // console.log("Video metadata loaded, starting playback");
          videoRef.current.play().then(() => {
            // console.log("Video playback started successfully");
            setCameraLoading(false);
          }).catch(err => {
            console.error("Error starting video playback:", err);
            setCameraError(`Error starting video: ${err.message}`);
            setCameraLoading(false);
          });
        };
      } else {
        console.error("Video reference is null!");
        setCameraError("Video reference is not available. Please try again.");
        setCameraLoading(false);
      }
    } catch (error) {
      console.error("Camera access error:", error);
      setCameraError(
        error.name === "NotAllowedError"
          ? "Camera access denied. Please allow camera access and try again."
          : error.name === "NotFoundError"
            ? "No camera found. Please ensure your device has a camera."
            : `Error accessing camera: ${error.message || "Unknown error"}`
      );
      setCameraLoading(false);
    }
  }, []);

  // Function to stop camera
  const stopCamera = useCallback(() => {
    if (cameraStream) {
      cameraStream.getTracks().forEach(track => track.stop());
      setCameraStream(null);
    }
  }, [cameraStream]);

  // Capture photo from camera
  const capturePhoto = useCallback(() => {
    if (videoRef.current && canvasRef.current) {
      const video = videoRef.current;
      const canvas = canvasRef.current;

      // Set canvas dimensions to match video dimensions
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;

      // Draw the current video frame to the canvas
      const context = canvas.getContext('2d');
      context.drawImage(video, 0, 0, canvas.width, canvas.height);

      // Get the image data as a data URL
      const imageData = canvas.toDataURL('image/jpeg', 0.8);
      setCapturedImage(imageData);
      // Also store in direct state for guaranteed display
      setDirectVisitorImageSrc(imageData);

      // If we have a visitor selected for check-in, save to localStorage
      if (visitorToCheckIn?.id) {
        saveImageToLocalStorage(visitorToCheckIn.id, 'photo', imageData);
      }

      stopCamera();
      setShowCamera(false);
    } else {
      console.error('Video or canvas reference is not available');
      setCameraError("Could not capture image. Please try again.");
    }
  }, [stopCamera, visitorToCheckIn]);

  // Initialize the ID camera - MOVED ABOVE useEffect
  const startIdCamera = useCallback(async () => {
    setIdCameraLoading(true);
    setIdCameraError(null);

    try {
      // Use exact constraints for better device selection
      const constraints = {
        video: {
          width: { ideal: 1280, min: 640 },
          height: { ideal: 720, min: 480 },
          facingMode: { ideal: "environment" }
        }
      };

      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      // console.log("ID Camera access granted, stream:", stream);

      // Store stream and set to video element
      setIdCameraStream(stream);

      if (idVideoRef.current) {
        // console.log("Setting ID video source to stream");
        idVideoRef.current.srcObject = stream;
        idVideoRef.current.onloadedmetadata = () => {
          // console.log("ID Video metadata loaded, starting playback");
          idVideoRef.current.play().then(() => {
            // console.log("ID Video playback started successfully");
            setIdCameraLoading(false);
          }).catch(err => {
            console.error("Error starting ID video playback:", err);
            setIdCameraError(`Error starting video: ${err.message}`);
            setIdCameraLoading(false);
          });
        };
      } else {
        console.error("ID Video reference is null!");
        setIdCameraError("Video reference is not available. Please try again.");
        setIdCameraLoading(false);
      }
    } catch (error) {
      console.error("ID Camera access error:", error);
      setIdCameraError(
        error.name === "NotAllowedError"
          ? "Camera access denied. Please allow camera access and try again."
          : error.name === "NotFoundError"
            ? "No camera found. Please ensure your device has a camera."
            : `Error accessing camera: ${error.message || "Unknown error"}`
      );
      setIdCameraLoading(false);
    }
  }, []);

  // Function to stop ID camera
  const stopIdCamera = useCallback(() => {
    if (idCameraStream) {
      idCameraStream.getTracks().forEach(track => track.stop());
      setIdCameraStream(null);
    }
  }, [idCameraStream]);

  // Capture photo from ID camera
  const captureIdPhoto = useCallback(() => {
    if (idVideoRef.current && idCanvasRef.current) {
      const video = idVideoRef.current;
      const canvas = idCanvasRef.current;

      // Set canvas dimensions to match video dimensions
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;

      // Draw the current video frame to the canvas
      const context = canvas.getContext('2d');
      context.drawImage(video, 0, 0, canvas.width, canvas.height);

      // Get the image data as a data URL
      const imageData = canvas.toDataURL('image/jpeg', 0.8);
      setCapturedIdImage(imageData);
      // Also store in direct state for guaranteed display
      setDirectIdDocumentImageSrc(imageData);

      // If we have a visitor selected for check-in, save to localStorage
      if (visitorToCheckIn?.id) {
        saveImageToLocalStorage(visitorToCheckIn.id, 'id_document', imageData);
      }

      stopIdCamera();
      setShowIdCamera(false);
    } else {
      console.error('ID video or canvas reference is not available');
      setIdCameraError("Could not capture image. Please try again.");
    }
  }, [stopIdCamera, visitorToCheckIn]);

  // Function to compress image data to avoid 413 Payload Too Large errors
  const compressImage = (imageData, maxWidth = 800, maxHeight = 600, quality = 0.7) => {
    return new Promise((resolve, reject) => {
      try {
        const img = new Image();

        img.onload = () => {
          try {
            let width = img.width;
            let height = img.height;

            if (width > maxWidth || height > maxHeight) {
              const ratio = Math.min(maxWidth / width, maxHeight / height);
              width = Math.floor(width * ratio);
              height = Math.floor(height * ratio);
            }

            const canvas = document.createElement('canvas');
            canvas.width = width;
            canvas.height = height;

            const ctx = canvas.getContext('2d');
            ctx.drawImage(img, 0, 0, width, height);

            const compressedData = canvas.toDataURL('image/jpeg', quality);
            resolve(compressedData);
          } catch (canvasError) {
            console.error('Error processing image on canvas:', canvasError);
            try {
              const canvas = document.createElement('canvas');
              canvas.width = img.width;
              canvas.height = img.height;
              const ctx = canvas.getContext('2d');
              ctx.drawImage(img, 0, 0);
              const fallbackData = canvas.toDataURL('image/jpeg', 0.5);
              resolve(fallbackData);
            } catch (fallbackError) {
              console.error('Fallback compression also failed:', fallbackError);
              reject(fallbackError);
            }
          }
        };

        img.onerror = (err) => {
          console.error('Error loading image for compression:', err);
          reject(new Error('Failed to load image for compression'));
        };

        img.src = imageData;
      } catch (error) {
        console.error('Error in image compression:', error);
        reject(error);
      }
    });
  };

  // Initial data loading effect - make it safe and reliable
  useEffect(() => {
    let isMounted = true;
    let retryCount = 0;
    const MAX_RETRIES = 3;
    let intervalId = null;

    const loadVisitors = async () => {
      try {
        if (!isMounted) return;

        setLoading(true);
        setError(null);

        const user = JSON.parse(sessionStorage.getItem('user') || '{}');
        const organizationId = user.organization_id;

        if (!organizationId) {
          setError('Organization context not found. Please try logging out and back in.');
          setLoading(false);
          return;
        }

        // Common request headers
        const headers = {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        };

        // Force localhost in development, ignore environment variable if it points to production
        const baseUrl = (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
          ? 'https://nivaas.app'
          : (process.env.REACT_APP_API_URL || 'https://nivaas.app');

        // Load units first if not already loaded
        if (!unitsLoaded) {
          try {
            // console.log('Loading units...'); // Reduced logging
            const unitsResponse = await axios.get(`${baseUrl}/api/units`, { headers });
            if (isMounted && unitsResponse?.data) {
              try {
                const processedUnits = Array.isArray(unitsResponse.data)
                  ? unitsResponse.data.map(unit => processUnitData(unit || {}))
                  : [];
                setLocalVillas(processedUnits);
                setUnitsLoaded(true);
                // console.log('Units loaded successfully:', processedUnits.length); // Reduced logging
              } catch (processingError) {
                console.error('Error processing units data:', processingError);
                // Continue with empty units rather than failing
                setLocalVillas([]);
              }
            }
          } catch (unitsError) {
            console.error('Error loading units:', unitsError);
            if (retryCount < MAX_RETRIES) {
              retryCount++;
              console.log(`Retrying units load... Attempt ${retryCount}`);
              await new Promise(resolve => setTimeout(resolve, 1000));
              if (isMounted) {
                return loadVisitors();
              }
              return;
            }
            setError('Failed to load units. Please refresh the page.');
            setLoading(false);
            return;
          }
        }

        // Get visitors with status 'active', 'approved', or 'upcoming'
        try {
          // console.log('Fetching active, approved, and upcoming visitors...'); // Reduced logging
          const [activeResponse, requestedResponse] = await Promise.all([
            axios.get(`${baseUrl}/api/visitors/search`, {
              params: {
                status: ['active', 'approved', 'upcoming'],
                include_organization: true,
                organization_id: organizationId,
                limit: 500
              },
              headers
            }),
            visitorAPI.getRequestedVisitors({
              page: requestedVisitorsPagination.currentPage
            })
          ]);

          // Process active visitors
          const responseData = activeResponse?.data || {};
          const activeVisitors = Array.isArray(responseData) ? responseData :
            responseData?.visitors ? responseData.visitors : [];

          // Process requested visitors
          const requestedData = requestedResponse?.data || {};
          const requestedVisitorsData = Array.isArray(requestedData) ? requestedData :
            requestedData?.visitors ? requestedData.visitors : [];

          // Update pagination info for requested visitors
          if (requestedData.total !== undefined) {
            setRequestedVisitorsPagination(prev => ({
              ...prev,
              total: requestedData.total,
              totalPages: requestedData.totalPages || Math.ceil(requestedData.total / 20), // Use static limit of 20
              currentPage: requestedData.page || prev.currentPage,
              limit: requestedData.limit || 20 // Use limit from backend response
            }));
          }

          // Debug log visitor data - only when there are issues
          if (activeVisitors.length === 0) {
            console.log('No active visitors found');
          } else {
            // console.log('Loaded visitors:', activeVisitors.length); // Reduced logging
          }

          if (requestedVisitorsData.length === 0) {
            console.log('No requested visitors found');
          } else {
            console.log('Loaded requested visitors:', requestedVisitorsData.length);
          }

          if (isMounted) {
            // Update local state with active visitors
            setLocalVisitors(activeVisitors);
            setRequestedVisitors(requestedVisitorsData);
            setError(null);
          }
        } catch (visitorError) {
          console.error('Error fetching visitors:', visitorError);
          if (isMounted) {
            setError('Failed to load visitor data. Please try again.');
          }
        }
      } catch (err) {
        console.error('Error in load function:', err);
        if (isMounted) {
          setError('An unexpected error occurred. Please refresh the page.');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    // Load visitors immediately and set up refresh interval
    loadVisitors();
    intervalId = setInterval(loadVisitors, 120000); // Refresh every 2 minutes (reduced from 30 seconds)

    return () => {
      isMounted = false;
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [unitsLoaded, requestedVisitorsPagination.currentPage]); // Add pagination dependency

  // Effect to handle units loading state
  useEffect(() => {
    if (!unitsLoaded && localVillas.length > 0) {
      setUnitsLoaded(true);
      console.log('Units loaded state updated:', localVillas.length);
    }
  }, [localVillas, unitsLoaded]);

  // Effect for camera setup - make sure all dependencies are provided
  useEffect(() => {
    if (showCamera && !cameraStream && !cameraError && !showFileUploadFallback) {
      // Slight delay to ensure modal is fully mounted
      const timer = setTimeout(() => {
        startCamera();
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [showCamera, cameraStream, cameraError, showFileUploadFallback, startCamera]);

  // Effect for ID camera setup - make sure all dependencies are provided
  useEffect(() => {
    if (showIdCamera && !idCameraStream && !idCameraError && !showIdFileUploadFallback) {
      // Slight delay to ensure modal is fully mounted
      const timer = setTimeout(() => {
        startIdCamera();
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [showIdCamera, idCameraStream, idCameraError, showIdFileUploadFallback, startIdCamera]);

  // Load data from the visitor service with improved active visitor handling
  useEffect(() => {
    try {
      // Skip processing if we don't have visitor data yet
      if (!Array.isArray(visitors) || visitors.length === 0) {
        return;
      }

      // Process visitors to fix active visitor statuses
      const processedVisitors = visitors.map(visitor => {
        // Ensure we're working with a valid visitor object
        if (!visitor || typeof visitor !== 'object') {
          console.warn('Received invalid visitor data:', visitor);
          return {
            id: null,
            visitor_name: 'Unknown',
            status: 'unknown',
            time_in: null,
            time_out: null
          };
        }

        const processed = processVisitorData(visitor);

        // In database terms: if time_in exists but time_out doesn't, visitor should be active
        if (processed.time_in && !processed.time_out && processed.status !== 'active') {
          try {
            if (processed.id) {
              localStorage.setItem(`visitor_status_${processed.id}`, 'active');
            }
          } catch (err) {
            console.error('Error saving visitor status to localStorage:', err);
          }

          return {
            ...processed,
            status: 'active'
          };
        }

        // Check localStorage for any overrides
        try {
          if (processed.id) {
            const savedStatus = localStorage.getItem(`visitor_status_${processed.id}`);
            if (savedStatus === 'active' && processed.time_in && !processed.time_out) {
              return {
                ...processed,
                status: 'active'
              };
            }
          }
        } catch (err) {
          console.error('Error reading visitor status from localStorage:', err);
        }

        return processed;
      });

      // Update local state with processed data
      setLocalVisitors(processedVisitors);

      // Process villas data if available
      if (Array.isArray(villas) && villas.length > 0) {
        try {
          const processedVillas = villas.map(villa => {
            // Handle null or undefined villa 
            if (!villa) return processUnitData({});
            return processUnitData(villa);
          });
          console.log("processedVillas", processedVillas);
          setLocalVillas(processedVillas);
          setUnitsLoaded(true);
        } catch (villaError) {
          console.error('Error processing villa data:', villaError);
        }
      }

      setError(dataError);
      setLoading(false);

    } catch (err) {
      console.error('Error processing visitor data:', err);
      setError('Failed to process data. Please try again.');
      setLoading(false);
    }
  }, [visitors, villas, dataError]);

  // Handle manual refresh
  const handleManualRefresh = () => {
    handleDataRefresh();
  };

  // Visitor status filtering functions - optimize to reduce calculation overhead
  const getActiveVisitors = useCallback(() => {
    return localVisitors.filter(visitor => {
      // A visitor is active if:
      // 1. Status is 'active' OR 'approved' AND has time_in AND no time_out
      // 2. Approved by owner and added by guard
      const isActive = (visitor.status === 'active' || (visitor.status === 'approved' && (visitor.time_in || visitor.approved_by))) && !visitor.time_out;

      if (isActive) {
        // console.log('Active visitor found:', {
        //   id: visitor.id,
        //   name: visitor.visitor_name,
        //   status: visitor.status,
        //   time_in: visitor.time_in,
        //   time_out: visitor.time_out
        // });
      }

      return isActive;
    });
  }, [localVisitors]);

  const getUpcomingVisitors = useCallback(() => {
    return localVisitors
      .map(processVisitorData)
      .filter(visitor => {
        // A visitor is upcoming if:
        // 1. Status is 'pending_approval' (waiting for owner approval)
        // 2. Status is 'approved' but not yet checked in by guard (regardless of who added/approved)
        // 3. Status is explicitly set to 'upcoming' (directly added by owners)
        const isPendingApproval = visitor.status === 'pending_approval';
        const isApprovedNotCheckedIn = visitor.status === 'approved' && !visitor.time_in;
        const isExplicitlyUpcoming = visitor.status === 'upcoming';

        const isUpcoming = (isPendingApproval || isApprovedNotCheckedIn || isExplicitlyUpcoming) && !visitor.time_out;

        if (isUpcoming) {
          // console.log('Found upcoming visitor:', {
          //   id: visitor.id,
          //   name: visitor.visitor_name,
          //   status: visitor.status,
          //   time_in: visitor.time_in,
          //   expected_date: visitor.expected_date,
          //   expected_time: visitor.expected_time,
          //   approved_by: visitor.approved_by || 'Not specified',
          //   created_by: visitor.created_by
          // });
        }

        return isUpcoming;
      })
      .sort((a, b) => {
        // Sort by status priority first
        const statusPriority = {
          'upcoming': 1,          // Show explicit upcoming first
          'pending_approval': 2,
          'approved': 3
        };

        const statusDiff = (statusPriority[a.status] || 99) - (statusPriority[b.status] || 99);
        if (statusDiff !== 0) return statusDiff;

        // Then sort by expected date/time or created date
        const dateA = new Date(a.expected_date || a.created_at);
        const dateB = new Date(b.expected_date || b.created_at);
        return dateA - dateB;
      });
  }, [localVisitors]);

  const getRequestedVisitors = useCallback(() => {
    return requestedVisitors
      .map(processVisitorData)
      .filter(visitor => {
        // A visitor is requested if they are waiting for owner approval
        const isRequested = visitor.status === 'pending_approval' && !visitor.time_out;

        return isRequested;
      })
      .sort((a, b) => {
        // Sort by creation date (newest first)
        const dateA = new Date(a.created_at);
        const dateB = new Date(b.created_at);
        return dateB - dateA;
      });
  }, [requestedVisitors]);

  // Helper: get comma-separated unit labels (e.g. DG-101, DG-102) from visitor.unit_ids or single villa_number
  const getVisitorUnitsLabel = useCallback((visitor) => {
    try {
      // If we have multiple unit_ids, map them to villa numbers
      if (visitor.unit_ids && Array.isArray(localVillas) && localVillas.length > 0) {
        const ids = String(visitor.unit_ids)
          .split(',')
          .map(id => id.trim())
          .filter(id => id);

        const labels = ids.map(id => {
          const numId = parseInt(id, 10);
          const villa = localVillas.find(v => v.id === numId);
          return villa?.villa_number || `Unit ${id}`;
        });

        if (labels.length > 0) {
          return labels.join(', ');
        }
      }

      // Fallback to single villa_number if available
      if (visitor.villa_number) {
        return visitor.villa_number;
      }

      return 'Unknown';
    } catch (e) {
      console.error('Error getting visitor units label:', e);
      return visitor.villa_number || 'Unknown';
    }
  }, [localVillas]);

  // Function to handle requested visitors pagination
  const handleRequestedVisitorsPageChange = useCallback((newPage) => {
    setRequestedVisitorsPagination(prev => ({
      ...prev,
      currentPage: newPage
    }));
  }, []);

  // Function to reset pagination when switching tabs
  const handleTabChange = useCallback((tab) => {
    setActiveTab(tab);
    if (tab === 'requested') {
      // Reset pagination to first page when switching to requested visitors tab
      setRequestedVisitorsPagination(prev => ({
        ...prev,
        currentPage: 1
      }));
    }
    if (tab === 'daily-visitors') {
      // Reset pagination and fetch daily visitors
      setDailyVisitorsPagination(prev => ({
        ...prev,
        currentPage: 1
      }));
      fetchDailyVisitors(1, dailyVisitorsSearch);
    }
  }, [dailyVisitorsSearch]);

  // ========== Daily Visitors Functions ==========
  
  // Fetch daily visitors with search and pagination
  const fetchDailyVisitors = useCallback(async (page = 1, search = '') => {
    setDailyVisitorsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const baseUrl = (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
        ? 'https://nivaas.app'
        : (process.env.REACT_APP_API_URL || 'https://nivaas.app');
      
      const response = await axios.get(`${baseUrl}/api/daily-visitors/search`, {
        params: {
          page,
          limit: 10,
          search
        },
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.data.success) {
        setDailyVisitors(response.data.dailyVisitors || []);
        setDailyVisitorsPagination({
          currentPage: response.data.page || 1,
          totalPages: response.data.totalPages || 0,
          total: response.data.total || 0,
          limit: response.data.limit || 10
        });
      }
    } catch (error) {
      console.error('Error fetching daily visitors:', error);
      toast.error('Failed to fetch daily visitors');
    } finally {
      setDailyVisitorsLoading(false);
    }
  }, []);

  // Handle daily visitors search
  const handleDailyVisitorsSearch = useCallback((e) => {
    const searchValue = e.target.value;
    setDailyVisitorsSearch(searchValue);
    // Debounce search
    const timeoutId = setTimeout(() => {
      fetchDailyVisitors(1, searchValue);
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [fetchDailyVisitors]);

  // Handle daily visitors pagination
  const handleDailyVisitorsPageChange = useCallback((newPage) => {
    setDailyVisitorsPagination(prev => ({
      ...prev,
      currentPage: newPage
    }));
    fetchDailyVisitors(newPage, dailyVisitorsSearch);
  }, [fetchDailyVisitors, dailyVisitorsSearch]);

  // Open modal to add new daily visitor
  const openAddDailyVisitorModal = useCallback(() => {
    setDailyVisitorModalMode('add');
    setEditingDailyVisitor(null);
    setDailyVisitorFormData({ name: '', contact: '', type: '', unit_ids: [], photo: null, id_document: null });
    setDailyVisitorCapturedPhoto(null);
    setDailyVisitorCapturedIdDoc(null);
    setDailyVisitorPhotoFile(null);
    setDailyVisitorIdDocFile(null);
    setDailyVisitorPhotoRemoved(false);
    setDailyVisitorIdDocRemoved(false);
    setShowDailyVisitorModal(true);
  }, []);

  // Open modal to edit daily visitor
  const openEditDailyVisitorModal = useCallback((visitor) => {
    setDailyVisitorModalMode('edit');
    setEditingDailyVisitor(visitor);
    setDailyVisitorFormData({
      name: visitor.name || '',
      contact: visitor.contact || '',
      type: visitor.type || '',
      unit_ids: visitor.unit_ids
        ? String(visitor.unit_ids)
            .split(',')
            .map(id => id.trim())
            .filter(id => id)
        : [],
      photo: null,
      id_document: null
    });
    setDailyVisitorCapturedPhoto(visitor.photo_url || null);
    setDailyVisitorCapturedIdDoc(visitor.document_id_url || null);
    setDailyVisitorPhotoFile(null);
    setDailyVisitorIdDocFile(null);
    setDailyVisitorPhotoRemoved(false);
    setDailyVisitorIdDocRemoved(false);
    setShowDailyVisitorModal(true);
  }, []);

  // Close daily visitor modal
  const closeDailyVisitorModal = useCallback(() => {
    setShowDailyVisitorModal(false);
    setEditingDailyVisitor(null);
    setDailyVisitorFormData({ name: '', contact: '', type: '', unit_ids: [], photo: null, id_document: null });
    setDailyVisitorCapturedPhoto(null);
    setDailyVisitorCapturedIdDoc(null);
    setDailyVisitorPhotoRemoved(false);
    setDailyVisitorIdDocRemoved(false);
    stopDailyVisitorCamera();
    stopDailyVisitorIdCamera();
  }, []);

  // Handle daily visitor form input change
  const handleDailyVisitorInputChange = useCallback((e) => {
    const { name, value } = e.target;
    setDailyVisitorFormData(prev => ({ ...prev, [name]: value }));
  }, []);

  // Camera functions for daily visitor
  const startDailyVisitorCamera = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
      setDailyVisitorCameraStream(stream);
      if (dailyVisitorVideoRef.current) {
        dailyVisitorVideoRef.current.srcObject = stream;
      }
      setShowDailyVisitorCamera(true);
    } catch (error) {
      console.error('Error accessing camera:', error);
      toast.error('Unable to access camera');
    }
  }, []);

  const stopDailyVisitorCamera = useCallback(() => {
    if (dailyVisitorCameraStream) {
      dailyVisitorCameraStream.getTracks().forEach(track => track.stop());
      setDailyVisitorCameraStream(null);
    }
    setShowDailyVisitorCamera(false);
  }, [dailyVisitorCameraStream]);

  const captureDailyVisitorPhoto = useCallback(() => {
    if (dailyVisitorVideoRef.current && dailyVisitorCanvasRef.current) {
      const canvas = dailyVisitorCanvasRef.current;
      const video = dailyVisitorVideoRef.current;
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(video, 0, 0);
      const dataUrl = canvas.toDataURL('image/jpeg', 0.8);
      setDailyVisitorCapturedPhoto(dataUrl);
      stopDailyVisitorCamera();
    }
  }, [stopDailyVisitorCamera]);

  // ID document camera functions
  const startDailyVisitorIdCamera = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
      setDailyVisitorIdCameraStream(stream);
      if (dailyVisitorIdVideoRef.current) {
        dailyVisitorIdVideoRef.current.srcObject = stream;
      }
      setShowDailyVisitorIdCamera(true);
    } catch (error) {
      console.error('Error accessing camera:', error);
      toast.error('Unable to access camera');
    }
  }, []);

  const stopDailyVisitorIdCamera = useCallback(() => {
    if (dailyVisitorIdCameraStream) {
      dailyVisitorIdCameraStream.getTracks().forEach(track => track.stop());
      setDailyVisitorIdCameraStream(null);
    }
    setShowDailyVisitorIdCamera(false);
  }, [dailyVisitorIdCameraStream]);

  const captureDailyVisitorIdDoc = useCallback(() => {
    if (dailyVisitorIdVideoRef.current && dailyVisitorIdCanvasRef.current) {
      const canvas = dailyVisitorIdCanvasRef.current;
      const video = dailyVisitorIdVideoRef.current;
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(video, 0, 0);
      const dataUrl = canvas.toDataURL('image/jpeg', 0.8);
      setDailyVisitorCapturedIdDoc(dataUrl);
      stopDailyVisitorIdCamera();
    }
  }, [stopDailyVisitorIdCamera]);

  // Save daily visitor (add or edit)
  const saveDailyVisitor = useCallback(async (e) => {
    e.preventDefault();
    
    if (!dailyVisitorFormData.name.trim()) {
      toast.error('Name is required');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const baseUrl = (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
        ? 'https://nivaas.app'
        : (process.env.REACT_APP_API_URL || 'https://nivaas.app');
      
      const formData = new FormData();
      formData.append('name', dailyVisitorFormData.name.trim());
      if (dailyVisitorFormData.contact.trim()) {
        formData.append('contact', dailyVisitorFormData.contact.trim());
      }
      if (dailyVisitorFormData.type.trim()) {
        formData.append('type', dailyVisitorFormData.type.trim());
      }
      // Send unit_ids as an array of IDs; backend will normalize to CSV
      const unitIdsArray = Array.isArray(dailyVisitorFormData.unit_ids)
        ? dailyVisitorFormData.unit_ids
        : (dailyVisitorFormData.unit_ids ? String(dailyVisitorFormData.unit_ids).split(',').map(id => id.trim()) : []);
      unitIdsArray.forEach(id => formData.append('unit_ids', id));

      // Handle photo: prefer real File, fall back to captured base64, or mark for removal
      if (dailyVisitorPhotoFile) {
        formData.append('photo', dailyVisitorPhotoFile);
      } else if (dailyVisitorCapturedPhoto && dailyVisitorCapturedPhoto.startsWith('data:image')) {
        formData.append('photo', dailyVisitorCapturedPhoto);
      }
      if (dailyVisitorPhotoRemoved) {
        formData.append('remove_photo', 'true');
      }

      // Handle ID document: prefer real File, fall back to captured base64, or mark for removal
      if (dailyVisitorIdDocFile) {
        formData.append('id_document', dailyVisitorIdDocFile);
      } else if (dailyVisitorCapturedIdDoc && dailyVisitorCapturedIdDoc.startsWith('data:image')) {
        formData.append('id_document', dailyVisitorCapturedIdDoc);
      }
      if (dailyVisitorIdDocRemoved) {
        formData.append('remove_id_document', 'true');
      }

      let response;
      if (dailyVisitorModalMode === 'add') {
        response = await axios.post(`${baseUrl}/api/daily-visitors`, formData, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
      } else {
        response = await axios.put(`${baseUrl}/api/daily-visitors/${editingDailyVisitor.id}`, formData, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
      }

      if (response.data.success) {
        toast.success(dailyVisitorModalMode === 'add' ? 'Daily visitor added successfully' : 'Daily visitor updated successfully');
        closeDailyVisitorModal();
        fetchDailyVisitors(dailyVisitorsPagination.currentPage, dailyVisitorsSearch);
      }
    } catch (error) {
      console.error('Error saving daily visitor:', error);
      toast.error(error.response?.data?.message || 'Failed to save daily visitor');
    } finally {
      setLoading(false);
    }
  }, [dailyVisitorFormData, dailyVisitorCapturedPhoto, dailyVisitorCapturedIdDoc, dailyVisitorPhotoRemoved, dailyVisitorIdDocRemoved, dailyVisitorModalMode, editingDailyVisitor, closeDailyVisitorModal, fetchDailyVisitors, dailyVisitorsPagination.currentPage, dailyVisitorsSearch]);

  // Delete daily visitor
  const deleteDailyVisitor = useCallback(async (visitor) => {
    if (!window.confirm(`Are you sure you want to delete ${visitor.name}?`)) {
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const baseUrl = (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
        ? 'https://nivaas.app'
        : (process.env.REACT_APP_API_URL || 'https://nivaas.app');
      
      const response = await axios.delete(`${baseUrl}/api/daily-visitors/${visitor.id}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.data.success) {
        toast.success('Daily visitor deleted successfully');
        fetchDailyVisitors(dailyVisitorsPagination.currentPage, dailyVisitorsSearch);
      }
    } catch (error) {
      console.error('Error deleting daily visitor:', error);
      toast.error(error.response?.data?.message || 'Failed to delete daily visitor');
    } finally {
      setLoading(false);
    }
  }, [fetchDailyVisitors, dailyVisitorsPagination.currentPage, dailyVisitorsSearch]);

  // Check-in daily visitor
  const checkInDailyVisitor = useCallback(async (visitor) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const baseUrl = (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
        ? 'https://nivaas.app'
        : (process.env.REACT_APP_API_URL || 'https://nivaas.app');
      
      const response = await axios.post(`${baseUrl}/api/daily-visitors/${visitor.id}/checkin`, {
        purpose: 'Daily Visit'
      }, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.data.success) {
        toast.success(`${visitor.name} checked in successfully`);
        fetchDailyVisitors(dailyVisitorsPagination.currentPage, dailyVisitorsSearch);
        // Also refresh main visitors list
        await refreshVisitors();
      }
    } catch (error) {
      console.error('Error checking in daily visitor:', error);
      toast.error(error.response?.data?.message || 'Failed to check in daily visitor');
    } finally {
      setLoading(false);
    }
  }, [fetchDailyVisitors, dailyVisitorsPagination.currentPage, dailyVisitorsSearch, refreshVisitors]);

  // Check-out daily visitor
  const checkOutDailyVisitor = useCallback(async (visitor) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const baseUrl = (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
        ? 'https://nivaas.app'
        : (process.env.REACT_APP_API_URL || 'https://nivaas.app');
      
      const response = await axios.post(`${baseUrl}/api/daily-visitors/${visitor.id}/checkout`, {}, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.data.success) {
        toast.success(`${visitor.name} checked out successfully`);
        fetchDailyVisitors(dailyVisitorsPagination.currentPage, dailyVisitorsSearch);
        // Also refresh main visitors list
        await refreshVisitors();
      }
    } catch (error) {
      console.error('Error checking out daily visitor:', error);
      toast.error(error.response?.data?.message || 'Failed to check out daily visitor');
    } finally {
      setLoading(false);
    }
  }, [fetchDailyVisitors, dailyVisitorsPagination.currentPage, dailyVisitorsSearch, refreshVisitors]);

  // Get status badge for daily visitor
  const getDailyVisitorStatusBadge = useCallback((visitor) => {
    if (visitor.current_status === 'checked_in') {
      return <span className="badge bg-success">Checked In</span>;
    } else if (visitor.current_status === 'checked_out') {
      return <span className="badge bg-secondary">Checked Out</span>;
    }
    return <span className="badge bg-warning text-dark">Not Checked In</span>;
  }, []);

  // Initial load of daily visitors when tab is active
  useEffect(() => {
    if (activeTab === 'daily-visitors') {
      fetchDailyVisitors(dailyVisitorsPagination.currentPage, dailyVisitorsSearch);
    }
  }, [activeTab]); // Only on tab change

  const getTodayExpectedVisitors = useCallback(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0); // normalize to midnight

    return localVisitors
      .map(processVisitorData) // keep this if needed
      .filter(visitor => {
        if (!visitor.expected_date || visitor.computed_status !== 'upcoming') return false;

        const expectedDate = new Date(visitor.expected_date);
        expectedDate.setHours(0, 0, 0, 0); // normalize for comparison

        return expectedDate.getTime() === today.getTime();
      })
      .slice(0, 5); // limit to max 5 results
  }, [localVisitors]);

  const getApprovedVisitors = useCallback(() => {
    const today = new Date();
    const todayMonth = today.getMonth() + 1; // getMonth() is 0-indexed
    const todayDay = today.getDate();
    const todayYear = today.getFullYear();

    return localVisitors.filter(v => {
      // Include both approved and upcoming visitors that haven't been checked in yet
      const isApprovedOrUpcoming = (v.status === 'approved' || v.status === 'upcoming') && !v.time_in;

      // Check if the visitor is expected today
      let isToday = false;
      if (v.expected_date) {
        const expectedDate = new Date(v.expected_date);
        isToday = expectedDate.getFullYear() === todayYear &&
          expectedDate.getMonth() + 1 === todayMonth &&
          expectedDate.getDate() === todayDay;
      }

      return isApprovedOrUpcoming && isToday;
    });
  }, [localVisitors]);

  // Phone validation state
  const [phoneError, setPhoneError] = useState('');
  const [isPhoneValid, setIsPhoneValid] = useState(true);

  // Visitor suggestion state
  const [visitorSuggestions, setVisitorSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);

  // Ref for phone input container
  const phoneInputRef = useRef(null);

  // Duration formatting function
  const formatDuration = (hours) => {
    const numHours = parseFloat(hours);
    if (numHours < 1) {
      return `${numHours * 60} minutes`;
    } else if (numHours < 24) {
      return `${numHours} hour${numHours !== 1 ? 's' : ''}`;
    } else if (numHours < 168) {
      const days = Math.floor(numHours / 24);
      return `${days} day${days !== 1 ? 's' : ''}`;
    } else if (numHours < 720) {
      const weeks = Math.floor(numHours / 168);
      return `${weeks} week${weeks !== 1 ? 's' : ''}`;
    } else {
      return '1 month';
    }
  };

  // Phone validation function
  const validatePhone = (phone) => {
    if (!phone) {
      setPhoneError('');
      setIsPhoneValid(true);
      return true;
    }

    // Remove any non-digit characters for validation
    const cleanPhone = phone.replace(/\D/g, '');

    // Check if it's exactly 10 digits
    if (cleanPhone.length !== 10) {
      setPhoneError('Phone number must be exactly 10 digits');
      setIsPhoneValid(false);
      return false;
    }

    // Check if it starts with 6, 7, 8, or 9 (Indian mobile numbers)
    if (!['6', '7', '8', '9'].includes(cleanPhone[0])) {
      setPhoneError('Phone number must start with 6, 7, 8, or 9');
      setIsPhoneValid(false);
      return false;
    }

    setPhoneError('');
    setIsPhoneValid(true);
    return true;
  };

  // Function to fetch visitor suggestions based on phone number
  const fetchVisitorSuggestions = async (phone) => {
    if (!phone || phone.length < 3) {
      setVisitorSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    try {
      setLoadingSuggestions(true);
      const result = await visitorAPI.getVisitorSuggestions(phone);

      if (result.success) {
        setVisitorSuggestions(result.data.data || []);
        setShowSuggestions(result.data.data && result.data.data.length > 0);
      } else {
        setVisitorSuggestions([]);
        setShowSuggestions(false);
      }
    } catch (error) {
      console.error('Error fetching visitor suggestions:', error);
      setVisitorSuggestions([]);
      setShowSuggestions(false);
    } finally {
      setLoadingSuggestions(false);
    }
  };

  // Function to handle visitor selection from suggestions
  const handleVisitorSelection = async (visitor) => {
    try {
      // Get full visitor details
      const result = await visitorAPI.getVisitorDetails(visitor.id);

      if (result.success) {
        const visitorData = result.data.data;

        // Debug logging for image URLs
        console.log('Visitor data received:', {
          id: visitorData.id,
          name: visitorData.visitor_name,
          photo_url: visitorData.photo_url,
          id_document_url: visitorData.id_document_url,
          photo_url_type: typeof visitorData.photo_url,
          id_document_url_type: typeof visitorData.id_document_url
        });

        // Auto-fill the form with visitor data
        setFormData(prev => ({
          ...prev,
          visitor_name: visitorData.visitor_name || '',
          phone: visitorData.phone || '',
          purpose: visitorData.purpose || '',
          expected_duration: visitorData.expected_duration || '1',
          unit_id: visitorData.unit_id || '',
          notes: visitorData.notes || ''
        }));

        // Set captured images if available, otherwise clear them
        // Store the original URLs for form submission (convert full URLs to relative URLs)
        if (visitorData.photo_url && visitorData.photo_url !== 'NULL' && visitorData.photo_url.trim() !== '') {
          setCapturedImage(visitorData.photo_url);
          // Convert full URL to relative URL for database storage
          let relativePhotoUrl = visitorData.photo_url;
          if (relativePhotoUrl.startsWith('https://nivaas.app')) {
            relativePhotoUrl = relativePhotoUrl.replace('https://nivaas.app', '');
          }
          setFormData(prev => ({ ...prev, original_photo_url: relativePhotoUrl }));
          console.log('Converted photo URL:', visitorData.photo_url, '→', relativePhotoUrl);
        } else {
          setCapturedImage(null);
          setFormData(prev => ({ ...prev, original_photo_url: null }));
        }

        if (visitorData.id_document_url && visitorData.id_document_url !== 'NULL' && visitorData.id_document_url.trim() !== '') {
          setCapturedIdImage(visitorData.id_document_url);
          // Convert full URL to relative URL for database storage
          let relativeIdDocUrl = visitorData.id_document_url;
          if (relativeIdDocUrl.startsWith('https://nivaas.app')) {
            relativeIdDocUrl = relativeIdDocUrl.replace('https://nivaas.app', '');
          }
          setFormData(prev => ({ ...prev, original_id_document_url: relativeIdDocUrl }));
          console.log('Converted ID document URL:', visitorData.id_document_url, '→', relativeIdDocUrl);
        } else {
          setCapturedIdImage(null);
          setFormData(prev => ({ ...prev, original_id_document_url: null }));
        }

        // Hide suggestions
        setShowSuggestions(false);
        setVisitorSuggestions([]);

        toast.success('Visitor details auto-filled successfully!');
      } else {
        toast.error('Failed to load visitor details');
      }
    } catch (error) {
      console.error('Error loading visitor details:', error);
      toast.error('Failed to load visitor details');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name === 'phone') {
      // Only allow numeric characters and limit to 10 digits
      const numericValue = value.replace(/\D/g, '').slice(0, 10);
      setFormData(prev => ({ ...prev, [name]: numericValue }));
      validatePhone(numericValue);

      // Fetch suggestions when phone number is entered (after 3 digits)
      if (numericValue.length >= 3) {
        fetchVisitorSuggestions(numericValue);
      } else {
        setShowSuggestions(false);
        setVisitorSuggestions([]);
      }
    } else if (name === 'unit_id') {
      // Find the selected unit
      const selectedUnit = localVillas.find(villa => villa.id?.toString() === value);
      let requiresApproval = false;
      if (selectedUnit && typeof selectedUnit.visitor_approval_required !== 'undefined') {
        requiresApproval = selectedUnit.visitor_approval_required === 1;
      }
      setFormData(prev => ({
        ...prev,
        unit_id: value,
        requires_approval: requiresApproval
      }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  // Add status badge function
  const getStatusBadge = (status) => {
    switch (status?.toLowerCase()) {
      case 'pending_approval':
        return <span className="badge bg-warning">Pending Owner Approval</span>;
      case 'active':
        return <span className="badge bg-success">Active</span>;
      case 'rejected':
        return <span className="badge bg-danger">Rejected</span>;
      case 'completed':
        return <span className="badge bg-secondary">Completed</span>;
      case 'upcoming':
        return <span className="badge bg-info">Upcoming</span>;
      case 'approved':
        return <span className="badge bg-success">Approved</span>;
      default:
        return <span className="badge bg-info">{status || 'Unknown'}</span>;
    }
  };

  // Click outside handler to close suggestions
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (phoneInputRef.current && !phoneInputRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };

    if (showSuggestions) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showSuggestions]);

  // Modify the handleCheckIn function to implement a two-step approach for photo uploads
  const handleCheckIn = async (e) => {
    if (e) {
      e.preventDefault();
    }

    if (!checkDataLoaded(localVillas)) {
      toast.error('Please wait for data to load completely');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Validate required fields
      if (!formData.visitor_name?.trim()) {
        toast.error('Please enter the visitor name');
        setError('Please enter the visitor name');
        setLoading(false);
        return;
      }

      if (!formData.unit_id) {
        toast.error('Please select a unit');
        setError('Please select a unit');
        setLoading(false);
        return;
      }

      if (!formData.purpose?.trim()) {
        toast.error('Please enter the purpose of visit');
        setError('Please enter the purpose of visit');
        setLoading(false);
        return;
      }

      // Validate phone number if provided
      if (formData.phone && !validatePhone(formData.phone)) {
        toast.error('Please enter a valid 10-digit mobile number');
        setError('Please enter a valid 10-digit mobile number');
        setLoading(false);
        return;
      }

      // Determine the correct status based on requires_approval
      const isApprovalRequired = formData.requires_approval === true;

      // Using the imported formatMySQLDateTime function from the top of the file
      // Create visitor data object with simplified status
      const visitorData = {
        ...formData,
        visitor_name: formData.visitor_name.trim(),
        purpose: formData.purpose.trim(),
        phone: formData.phone?.trim(),
        status: isApprovalRequired ? 'pending_approval' : 'active',
        time_in: isApprovalRequired ? null : new Date(),
        expected_date: formatMySQLDate(new Date()),
        expected_time: new Date().toTimeString().split(' ')[0],
        requires_approval: isApprovalRequired
      };

      // Process images if available - using FormData approach
      let photoFile = null;
      let idDocFile = null;
      let hasOriginalUrls = false;

      // Check if we have original URLs from previous visitor (for auto-fill)
      if (formData.original_photo_url || formData.original_id_document_url) {
        hasOriginalUrls = true;
        console.log('Found original URLs from previous visitor:', {
          photo_url: formData.original_photo_url,
          id_document_url: formData.original_id_document_url
        });
      }

      if (capturedImage) {
        // Check if this is a URL (from previous visitor) or actual image data
        if (capturedImage.startsWith('http') || capturedImage.startsWith('/api/')) {
          // This is a URL from previous visitor, we'll handle it specially
          console.log('Detected URL from previous visitor:', capturedImage);
          hasOriginalUrls = true;
        } else {
          // This is actual image data, convert to file
          photoFile = dataURLToFile(
            capturedImage,
            `visitor_photo_${Date.now()}.jpg`
          );
          console.log('Photo file created:', photoFile?.name, photoFile?.size, 'bytes');
        }
      }

      if (capturedIdImage) {
        // Check if this is a URL (from previous visitor) or actual image data
        if (capturedIdImage.startsWith('http') || capturedIdImage.startsWith('/api/')) {
          // This is a URL from previous visitor, we'll handle it specially
          console.log('Detected ID document URL from previous visitor:', capturedIdImage);
          hasOriginalUrls = true;
        } else {
          // This is actual image data, convert to file
          idDocFile = dataURLToFile(
            capturedIdImage,
            `visitor_id_${Date.now()}.jpg`
          );
          console.log('ID document file created:', idDocFile?.name, idDocFile?.size, 'bytes');
        }
      }

      // Step 1: First create the visitor record WITHOUT images
      // We'll include only the basic data in the initial creation request
      const createData = { ...visitorData };
      delete createData.photo;
      delete createData.id_document;

      console.log('Step 1: Creating visitor record with data:', {
        ...createData,
        photo: 'PHOTO_DATA_OMITTED',
        id_document: 'ID_DOC_DATA_OMITTED'
      });

      try {
        const createResponse = await visitorAPI.createVisitor(createData);
        console.log('Visitor creation response:', createResponse?.data);

        if (createResponse?.data) {
          // Extract visitor ID from response
          let visitorId = null;

          if (createResponse.data.visitor && createResponse.data.visitor.id) {
            visitorId = createResponse.data.visitor.id;
          } else if (createResponse.data.id) {
            visitorId = createResponse.data.id;
          } else if (typeof createResponse.data === 'object') {
            visitorId = createResponse.data.id;
          }

          console.log('Extracted visitor ID:', visitorId);

          if (visitorId) {
            console.log('Visitor created successfully with ID:', visitorId);

            // Step 2A: Handle original URLs from previous visitor (if any)
            if (hasOriginalUrls) {
              console.log('Step 2A: Handling original URLs from previous visitor');

              try {
                // Update the visitor record with the original URLs
                const updateData = {};

                if (formData.original_photo_url) {
                  updateData.photo_url = formData.original_photo_url;
                  console.log('Setting original photo URL:', formData.original_photo_url);
                } else {
                  // Photo was removed, set to NULL
                  updateData.photo_url = null;
                  console.log('Photo was removed, setting photo_url to NULL');
                }

                if (formData.original_id_document_url) {
                  updateData.id_document_url = formData.original_id_document_url;
                  console.log('Setting original ID document URL:', formData.original_id_document_url);
                } else {
                  // ID document was removed, set to NULL
                  updateData.id_document_url = null;
                  console.log('ID document was removed, setting id_document_url to NULL');
                }

                if (Object.keys(updateData).length > 0) {
                  await axios.put(
                    `${process.env.REACT_APP_API_URL || 'https://nivaas.app'}/api/visitors/${visitorId}`,
                    updateData,
                    {
                      headers: {
                        'Authorization': `Bearer ${localStorage.getItem('token')}`,
                        'Content-Type': 'application/json'
                      }
                    }
                  );
                  console.log('Successfully updated visitor record with original URLs or NULL values');
                }
              } catch (error) {
                console.error('Error updating visitor with original URLs:', error);
              }
            }

            // Step 2B: Upload new images as a separate step with retry mechanism
            if (photoFile || idDocFile) {
              console.log('Step 2B: Uploading new images for visitor ID:', visitorId);

              // First save to localStorage as backup immediately
              if (capturedImage) {
                saveImageToLocalStorage(visitorId, 'photo', capturedImage);
                console.log('Photo saved to localStorage as backup');
              }

              if (capturedIdImage) {
                saveImageToLocalStorage(visitorId, 'id_document', capturedIdImage);
                console.log('ID document saved to localStorage as backup');
              }

              // Try uploading with our improved upload function
              const uploadResult = await uploadVisitorImages(visitorId, photoFile, idDocFile);
              console.log('Image upload result:', uploadResult);

              // Store the image URLs if available
              if (uploadResult && uploadResult.success) {
                console.log('Image upload success with URLs:', {
                  photoUrl: uploadResult.photoUrl,
                  idDocUrl: uploadResult.idDocUrl
                });

                // Store image URLs for later use
                if (uploadResult.photoUrl) {
                  localStorage.setItem(`visitor_${visitorId}_photo_url`, uploadResult.photoUrl);

                  // Also update the database with these URLs
                  try {
                    await axios.put(
                      `${process.env.REACT_APP_API_URL || 'https://nivaas.app'}/api/visitors/${visitorId}`,
                      { photo_url: uploadResult.photoUrl },
                      {
                        headers: {
                          'Authorization': `Bearer ${localStorage.getItem('token')}`,
                          'Content-Type': 'application/json'
                        }
                      }
                    );
                    console.log('Successfully updated visitor record with photo URL');
                  } catch (error) {
                    console.log('Error updating visitor record with photo URL:', error.message);
                  }
                }

                if (uploadResult.idDocUrl) {
                  localStorage.setItem(`visitor_${visitorId}_id_doc_url`, uploadResult.idDocUrl);

                  // Also update the database with these URLs
                  try {
                    await axios.put(
                      `${process.env.REACT_APP_API_URL || 'https://nivaas.app'}/api/visitors/${visitorId}`,
                      { id_document_url: uploadResult.idDocUrl },
                      {
                        headers: {
                          'Authorization': `Bearer ${localStorage.getItem('token')}`,
                          'Content-Type': 'application/json'
                        }
                      }
                    );
                    console.log('Successfully updated visitor record with ID document URL');
                  } catch (error) {
                    console.log('Error updating visitor record with ID document URL:', error.message);
                  }
                }
              }

              // If upload failed, try a direct method as last resort
              if ((!uploadResult || !uploadResult.success) && (capturedImage || capturedIdImage)) {
                console.log('Attempting direct image upload as final fallback');
                try {
                  const directResult = await saveImagesDirectlyToServer(visitorId, capturedImage, capturedIdImage);

                  if (directResult && directResult.success) {
                    console.log('Direct image upload successful with results:', directResult);

                    if (directResult.photoUrl) {
                      localStorage.setItem(`visitor_${visitorId}_photo_url`, directResult.photoUrl);

                      // Update database with photo URL
                      try {
                        await axios.put(
                          `${process.env.REACT_APP_API_URL || 'https://nivaas.app'}/api/visitors/${visitorId}`,
                          { photo_url: directResult.photoUrl },
                          {
                            headers: {
                              'Authorization': `Bearer ${localStorage.getItem('token')}`,
                              'Content-Type': 'application/json'
                            }
                          }
                        );
                        console.log('Successfully updated visitor record with direct photo URL');
                      } catch (error) {
                        console.log('Error updating visitor record with direct photo URL:', error.message);
                      }
                    }

                    if (directResult.idDocUrl) {
                      localStorage.setItem(`visitor_${visitorId}_id_doc_url`, directResult.idDocUrl);

                      // Update database with ID document URL
                      try {
                        await axios.put(
                          `${process.env.REACT_APP_API_URL || 'https://nivaas.app'}/api/visitors/${visitorId}`,
                          { id_document_url: directResult.idDocUrl },
                          {
                            headers: {
                              'Authorization': `Bearer ${localStorage.getItem('token')}`,
                              'Content-Type': 'application/json'
                            }
                          }
                        );
                        console.log('Successfully updated visitor record with direct ID document URL');
                      } catch (error) {
                        console.log('Error updating visitor record with direct ID document URL:', error.message);
                      }
                    }
                  }
                } catch (directError) {
                  console.log('Direct upload also failed, but images are in localStorage:', directError.message);
                }
              }
            }

            // Reset form and images
            resetForm();

            if (formData.requires_approval) {
              // For visitors requiring approval
              toast.success('Visitor registered successfully. Awaiting owner approval.');
              setActiveTab('upcoming');
            } else {
              // For direct check-ins
              toast.success('Visitor checked in successfully!');
              setCheckedInVisitor(createResponse.data.visitor || createResponse.data);
              setShowSuccess(true);
              setActiveTab('active');
              localStorage.setItem(ACTIVE_TAB_STORAGE_KEY, 'active');
              window.location.reload();
            }

            // Close modal if open
            setShowCheckInModal(false);

            // Refresh the visitors list
            await refreshVisitors();

            // Also refresh local visitors data to include requested visitors
            await handleDataRefresh();

            // Force a second refresh after a short delay to ensure lists are updated
            setTimeout(() => {
              refreshVisitors();
              handleDataRefresh();
            }, 1000);
          } else {
            // Handle the case where we couldn't extract a visitor ID
            console.error('Failed to extract visitor ID from response:', createResponse.data);
            throw new Error('Could not determine visitor ID from server response');
          }
        } else {
          const errorMessage = createResponse?.data?.message || 'Failed to register visitor';
          setError(errorMessage);
          toast.error(errorMessage);
        }
      } catch (createError) {
        console.error('Error creating visitor:', createError);
        const errorMessage = createError.message || 'An unexpected error occurred. Please try again.';
        setError(errorMessage);
        toast.error(errorMessage);
      }
    } catch (err) {
      console.error('Error in handleCheckIn:', err);
      const errorMessage = err.message || 'An unexpected error occurred. Please try again.';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // const handleCheckout = async () => {
  //   if (!checkoutId || loading) return;

  //   try {
  //     setLoading(true);
  //     // Ensure checkoutId is a string and valid
  //     const visitorIdToCheckout = checkoutId.toString().trim();

  //     console.log(`Initiating checkout for visitor ID: ${visitorIdToCheckout}`);

  //     // Find the visitor to check out
  //     const visitorToCheckout = localVisitors.find(v => v.id.toString() === visitorIdToCheckout);
  //     if (!visitorToCheckout) {
  //       console.error(`Visitor with ID ${visitorIdToCheckout} not found in local state`);
  //       setError(`Visitor with ID ${visitorIdToCheckout} not found. Please try again.`);
  //       setLoading(false);
  //       return;
  //     }

  //     // Explicitly check if the visitor is already checked out
  //     if (visitorToCheckout.time_out || visitorToCheckout.status === 'completed') {
  //       console.warn(`Visitor ${visitorIdToCheckout} has already been checked out.`);
  //       toast.info(`Visitor ${visitorIdToCheckout} has already been checked out.`);
  //       setLoading(false);
  //       setCheckoutId(''); // Clear the checkout ID
  //       return;
  //     }

  //     // Use the more reliable direct checkout method
  //     await handleCheckoutVisitor(visitorIdToCheckout);

  //     // Reset input field on success
  //       setCheckoutId('');

  //   } catch (err) {
  //     console.error('Error during checkout:', err);
  //     // Error handling is already managed in handleCheckoutVisitor
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  // Handle file upload as fallback for camera
  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setCapturedImage(event.target.result);
        setShowCamera(false);
        setShowFileUploadFallback(false);
      };
      reader.readAsDataURL(file);
    }
  };

  // Handle ID file upload as fallback for camera
  const handleIdFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setCapturedIdImage(e.target.result);
        setShowIdFileUploadFallback(false);
      };
      reader.readAsDataURL(file);
    }
  };

  // Date formatting is now handled by the imported utils
  // This function preserves the custom "Not yet checked out" message
  // const formatVisitorDateTime = (dateString) => {
  //   // For checkout time specifically (undefined or null means not checked out)
  //   if (!dateString) return 'Not yet checked out';

  //   // Let the main formatDateTime function handle all the special cases
  //   // Our custom utility from dateUtils.js is robust enough now
  //   return formatDateTime(dateString, true);
  // };

  function formatVisitorDateTime(dateString) {
    if (!dateString || typeof dateString !== 'string') return 'N/A';

    try {
      // Parse "YYYY-MM-DD HH:mm:ss" as UTC
      const [datePart, timePart] = dateString.split(' ');
      const [year, month, day] = datePart.split('-').map(Number);
      const [hour, minute, second] = timePart.split(':').map(Number);

      // Create a UTC Date object
      const utcDate = new Date(Date.UTC(year, month - 1, day, hour, minute, second));

      // Format to IST using timeZone
      return new Intl.DateTimeFormat('en-IN', {
        timeZone: 'Asia/Kolkata',
        year: 'numeric',
        month: 'short',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true
      }).format(utcDate);
    } catch (err) {
      console.error('Invalid date string:', dateString, err);
      return 'Invalid Date';
    }
  }

  // Duration calculation is now handled by the imported utils
  const calculateDuration = (timeIn, timeOut) => {
    if (!timeOut) return 'In Progress';
    return formatDuration(timeIn, timeOut);
  };

  // Handle submit button click with image compression
  const handleSubmitButtonClick = () => {
    console.log('Submit button clicked');
    try {
      setLoading(true);

      // Compress images before submission if available
      Promise.all([
        capturedImage ? compressImage(capturedImage, 800, 600, 0.8) : Promise.resolve(null),
        capturedIdImage ? compressImage(capturedIdImage, 800, 600, 0.8) : Promise.resolve(null)
      ])
        .then(([compressedPhoto, compressedIdDoc]) => {
          // Update the form data with compressed images
          if (compressedPhoto) {
            dataURLToFile(compressedPhoto, `visitor_photo_${Date.now()}.jpg`)
              .then(file => {
                console.log('Photo compressed for upload, new size:', file.size, 'bytes');
              });
          }

          if (compressedIdDoc) {
            dataURLToFile(compressedIdDoc, `visitor_id_${Date.now()}.jpg`)
              .then(file => {
                console.log('ID document compressed for upload, new size:', file.size, 'bytes');
              });
          }

          // Small delay to ensure files are set
          setTimeout(() => {
            handleCheckIn()
              .catch(error => {
                console.error('Error during check in:', error);
                setError('Error during check in. Please try again.');
              })
              .finally(() => setLoading(false));
          }, 500);
        })
        .catch(error => {
          console.error('Error compressing images:', error);
          // Proceed with uncompressed images
          handleCheckIn()
            .catch(error => {
              console.error('Error during check in:', error);
              setError('Error during check in. Please try again.');
            })
            .finally(() => setLoading(false));
        });
    } catch (e) {
      console.error('Error in submit handler:', e);
      setError('Error submitting form. Please try again.');
      setLoading(false);
    }
  };

  // Handle showing visitor details
  const handleShowDetails = async (visitorId) => {
    try {
      setLoading(true);

      // Reset direct image sources
      setDirectVisitorImageSrc('');
      setDirectIdDocumentImageSrc('');

      // Find the visitor in the local data (check both localVisitors and requestedVisitors)
      let visitorDetails = localVisitors.find(v => v.id === visitorId);

      if (!visitorDetails) {
        // If not found in localVisitors, check requestedVisitors
        visitorDetails = requestedVisitors.find(v => v.id === visitorId);
      }

      if (!visitorDetails) {
        toast.error('Visitor details not found in local data');
        setLoading(false);
        return;
      }

      // Set initial visitor details from local data immediately
      setSelectedVisitor(visitorDetails);
      setShowDetailsModal(true);

      // Step 1: Check localStorage for images first (fastest source)
      const storedPhoto = getImageFromLocalStorage(visitorId, 'photo');
      const storedIdDocument = getImageFromLocalStorage(visitorId, 'id_document');

      if (storedPhoto) {
        console.log('Found photo in localStorage, using it immediately');
        setDirectVisitorImageSrc(storedPhoto);
        visitorDetails.photo = storedPhoto;
      }

      if (storedIdDocument) {
        console.log('Found ID document in localStorage, using it immediately');
        setDirectIdDocumentImageSrc(storedIdDocument);
        visitorDetails.id_document = storedIdDocument;
      }

      // Get the user's organization ID from session storage
      const user = JSON.parse(sessionStorage.getItem('user') || '{}');
      const organizationId = user.organization_id;
      const token = localStorage.getItem('token');

      if (!organizationId || !token) {
        setLoading(false);
        return;
      }

      // Define the endpoint URL outside try block so it's accessible in catch block
      // Force localhost in development, ignore environment variable if it points to production
      const baseUrl = (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
        ? 'https://nivaas.app'
        : (process.env.REACT_APP_API_URL || 'https://nivaas.app');

      // Step 2: Try to get the most up-to-date data from server
      try {
        console.log(`Fetching visitor data from server for ID: ${visitorId}`);

        // Use the search endpoint with organization context and visitor ID
        const response = await axios.get(`${baseUrl}/api/visitors/search`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          params: {
            organization_id: organizationId,
            visitor_ids: [visitorId],  // Search for this specific visitor
            include_photos: true,
            limit: 1
          }
        });

        if (response.data && Array.isArray(response.data) && response.data.length > 0) {
          // Find the specific visitor in the response
          const serverVisitor = response.data[0];

          if (serverVisitor) {
            console.log('Server returned visitor data:', serverVisitor.visitor_name);

            // Process photo URLs if present
            let photoUrl = serverVisitor.photo_url;
            let idDocumentUrl = serverVisitor.id_document_url;

            // Process photo URL - use cache busting to avoid browser cache
            if (photoUrl) {
              try {
                const timestamp = new Date().getTime();
                const processedPhotoUrl = getImageUrl(photoUrl);
                const cacheBustedUrl = processedPhotoUrl.includes('?')
                  ? `${processedPhotoUrl}&t=${timestamp}`
                  : `${processedPhotoUrl}?t=${timestamp}`;

                console.log('Setting photo URL from server data');
                setDirectVisitorImageSrc(cacheBustedUrl);
              } catch (error) {
                console.log('Error processing photo URL:', error.message);
                // Keep using localStorage version if there's an error
              }
            }

            // Process ID document URL - use cache busting to avoid browser cache
            if (idDocumentUrl) {
              try {
                const timestamp = new Date().getTime();
                const processedIdUrl = getImageUrl(idDocumentUrl);
                const cacheBustedUrl = processedIdUrl.includes('?')
                  ? `${processedIdUrl}&t=${timestamp}`
                  : `${processedIdUrl}?t=${timestamp}`;

                console.log('Setting ID document URL from server data');
                setDirectIdDocumentImageSrc(cacheBustedUrl);
              } catch (error) {
                console.log('Error processing ID document URL:', error.message);
                // Keep using localStorage version if there's an error
              }
            }

            // Update visitor details with server data while preserving local data
            const mergedVisitor = {
              ...visitorDetails,
              ...serverVisitor,
              photo: serverVisitor.photo || visitorDetails.photo,
              photo_url: photoUrl || visitorDetails.photo_url,
              id_document: serverVisitor.id_document || visitorDetails.id_document,
              id_document_url: idDocumentUrl || visitorDetails.id_document_url
            };

            setSelectedVisitor(mergedVisitor);
          } else {
            console.log('Visitor not found in search results, using local data');
          }
        }
      } catch (error) {
        console.log('Error fetching visitor details:', error.message);
        // Don't close modal or show error since we have local data
        if (error.response?.status === 403) {
          console.log('Permission denied for visitor details, using local data only');
        }
      }

      // Step 3: Try an alternative direct endpoint as fallback if we don't have images yet
      if (!directVisitorImageSrc || !directIdDocumentImageSrc) {
        try {
          console.log('Trying alternative endpoint for visitor images');
          const altEndpoint = `${baseUrl}/api/visitors/${visitorId}`;

          const altResponse = await axios.get(altEndpoint, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });

          if (altResponse.data) {
            const altVisitor = altResponse.data;

            // Process photo URL if we still need it
            if (!directVisitorImageSrc && altVisitor.photo_url) {
              try {
                const timestamp = new Date().getTime();
                const processedPhotoUrl = getImageUrl(altVisitor.photo_url);
                const cacheBustedUrl = processedPhotoUrl.includes('?')
                  ? `${processedPhotoUrl}&t=${timestamp}`
                  : `${processedPhotoUrl}?t=${timestamp}`;

                console.log('Setting photo URL from alternative endpoint');
                setDirectVisitorImageSrc(cacheBustedUrl);
              } catch (error) {
                console.log('Error processing alt photo URL:', error.message);
              }
            }

            // Process ID document URL if we still need it
            if (!directIdDocumentImageSrc && altVisitor.id_document_url) {
              try {
                const timestamp = new Date().getTime();
                const processedIdUrl = getImageUrl(altVisitor.id_document_url);
                const cacheBustedUrl = processedIdUrl.includes('?')
                  ? `${processedIdUrl}&t=${timestamp}`
                  : `${processedIdUrl}?t=${timestamp}`;

                console.log('Setting ID document URL from alternative endpoint');
                setDirectIdDocumentImageSrc(cacheBustedUrl);
              } catch (error) {
                console.log('Error processing alt ID document URL:', error.message);
              }
            }
          }
        } catch (altError) {
          console.log('Alternative endpoint also failed:', altError.message);
        }
      }
    } catch (error) {
      console.log('Error in handleShowDetails:', error.message);
      toast.error('Error loading visitor details');
    } finally {
      setLoading(false);
    }
  };

  // Add close function for the details modal
  const handleCloseDetails = () => {
    setShowDetailsModal(false);
    setSelectedVisitor(null);
  };

  // Update processVisitorCheckIn for pre-registered visitors
  const processVisitorCheckIn = async () => {
    setLoading(true);

    try {
      if (!visitorToCheckIn) {
        setError('No visitor selected for check-in');
        setLoading(false);
        return;
      }

      // Create an updated visitor object for check-in
      const checkInData = {
        // If visitor was pre-approved or is being directly checked in, set status to active
        status: visitorToCheckIn.status === 'approved' || !formData.requires_approval ? 'active' : 'pending_approval',
        time_in: formatMySQLDateTime(new Date()),
        unit_id: visitorToCheckIn.unit_id,
        requires_approval: formData.requires_approval
      };

      console.log(`Checking in visitor ${visitorToCheckIn.id} with status: ${checkInData.status}`);

      // First save images to localStorage as an immediate backup in case API calls fail
      if (capturedImage) {
        try {
          saveImageToLocalStorage(visitorToCheckIn.id, 'photo', capturedImage);
          console.log('Photo saved to localStorage as backup');
        } catch (localStorageError) {
          console.log('LocalStorage error for photo:', localStorageError);
        }
      }

      if (capturedIdImage) {
        try {
          saveImageToLocalStorage(visitorToCheckIn.id, 'id_document', capturedIdImage);
          console.log('ID document saved to localStorage as backup');
        } catch (localStorageError) {
          console.log('LocalStorage error for ID document:', localStorageError);
        }
      }

      // Process images if available
      let photoFile = null;
      let idDocFile = null;

      if (capturedImage) {
        photoFile = dataURLToFile(
          capturedImage,
          `visitor_photo_${visitorToCheckIn.id}_${Date.now()}.jpg`
        );
        console.log('Photo file created for check-in:', photoFile?.name, photoFile?.size, 'bytes');
      }

      if (capturedIdImage) {
        idDocFile = dataURLToFile(
          capturedIdImage,
          `visitor_id_${visitorToCheckIn.id}_${Date.now()}.jpg`
        );
        console.log('ID document file created for check-in:', idDocFile?.name, idDocFile?.size, 'bytes');
      }

      // Step 1: First update the visitor status using the standard endpoint
      console.log('Step 1: Updating visitor status for visitor ID:', visitorToCheckIn.id);
      const checkInResult = await checkInVisitor(visitorToCheckIn.id, checkInData);

      if (checkInResult && (checkInResult.success || checkInResult.id)) {
        console.log('Check-in API call successful:', checkInResult);

        // Step 2: Upload images using our dedicated function
        let imageUploadSuccess = false;
        let photoUrl = null;
        let idDocUrl = null;

        if (photoFile || idDocFile) {
          console.log('Step 2: Uploading photos for visitor ID:', visitorToCheckIn.id);

          const uploadResult = await uploadVisitorImages(visitorToCheckIn.id, photoFile, idDocFile);
          console.log('Image upload result:', uploadResult);

          if (uploadResult && uploadResult.success) {
            imageUploadSuccess = true;

            // Store the image URLs if provided in the response
            if (uploadResult.photoUrl) {
              photoUrl = uploadResult.photoUrl;
              localStorage.setItem(`visitor_${visitorToCheckIn.id}_photo_url`, photoUrl);
            }

            if (uploadResult.idDocUrl) {
              idDocUrl = uploadResult.idDocUrl;
              localStorage.setItem(`visitor_${visitorToCheckIn.id}_id_doc_url`, idDocUrl);
            }
          }
        }

        // If upload failed, try direct update as a fallback
        if (!imageUploadSuccess && (capturedImage || capturedIdImage)) {
          console.log('Image upload failed, trying direct database update');

          const directUpdateResult = await saveImagesDirectlyToServer(
            visitorToCheckIn.id,
            capturedImage,
            capturedIdImage
          );

          console.log('Direct database update result:', directUpdateResult);

          if (directUpdateResult && directUpdateResult.success) {
            imageUploadSuccess = true;

            // Store the image URLs if provided in the response
            if (directUpdateResult.photoUrl) {
              photoUrl = directUpdateResult.photoUrl;
              localStorage.setItem(`visitor_${visitorToCheckIn.id}_photo_url`, photoUrl);
            }

            if (directUpdateResult.idDocUrl) {
              idDocUrl = directUpdateResult.idDocUrl;
              localStorage.setItem(`visitor_${visitorToCheckIn.id}_id_doc_url`, idDocUrl);
            }
          }
        }

        // Show appropriate success message
        toast.success(formData.requires_approval ?
          'Visitor checked in successfully. Awaiting owner approval.' :
          'Visitor checked in successfully.');

        // Handle success - pass image URLs to the success handler
        handleCheckInSuccess({
          ...visitorToCheckIn,
          ...checkInData,
          photo_url: photoUrl,
          id_document_url: idDocUrl
        });
      } else {
        // Handle error case
        console.error('Check-in API call failed:', checkInResult);
        setError('Failed to check in visitor. Please try again.');
        toast.error('Failed to check in visitor');
      }

    } catch (error) {
      console.error('Error processing visitor check-in:', error);
      setError(`Failed to check in visitor: ${error.message || 'Unknown error'}`);
      toast.error('Failed to check in visitor');
    } finally {
      setLoading(false);
    }
  };

  // Update handleCheckoutVisitor to use simplified status
  const handleCheckoutVisitor = async (visitorId) => {
    if (!visitorId || loading) return;

    try {
      setLoading(true);

      // Find the visitor to check out
      const visitorToCheckout = localVisitors.find(v => v.id.toString() === visitorId.toString());
      if (!visitorToCheckout) {
        setError(`Visitor with ID ${visitorId} not found. Please try again.`);
        setLoading(false);
        return;
      }

      // Modified check: Allow checkout of both active and approved visitors
      // If time_in is missing but status is approved, we'll auto-assign a time_in
      if (visitorToCheckout.status !== 'active' && visitorToCheckout.status !== 'approved') {
        toast.error('Only checked-in or approved visitors can be checked out');
        setLoading(false);
        return;
      }

      // If status is approved but no time_in, add one now
      let updatedVisitor = visitorToCheckout;
      if (visitorToCheckout.status === 'approved' && !visitorToCheckout.time_in) {
        // Use either approved_at time or current time
        const timeIn = visitorToCheckout.approved_at ||
          formatMySQLDateTime(new Date());

        console.log(`Auto-assigning time_in for approved visitor: ${timeIn}`);
        updatedVisitor = {
          ...visitorToCheckout,
          time_in: timeIn
        };

        // Update in local state before checkout
        setLocalVisitors(prev =>
          prev.map(v => v.id.toString() === visitorId.toString() ? updatedVisitor : v)
        );
      }

      // Explicitly check if the visitor is already checked out
      if (updatedVisitor.time_out || updatedVisitor.status === 'completed') {
        toast.info(`Visitor ${visitorId} has already been checked out.`);
        setLoading(false);
        return;
      }

      // Update UI state first for better user experience
      const updatedVisitors = localVisitors.map(v => {
        if (v.id.toString() === visitorId.toString()) {
          const now = new Date();
          removeVisitorActiveStatus(visitorId);
          return {
            ...v,
            time_out: formatMySQLDateTime(now),
            status: 'completed'
          };
        }
        return v;
      });

      setLocalVisitors(updatedVisitors);

      // Prepare checkout data
      const checkoutTime = formatMySQLDateTime(new Date());
      const checkoutData = {
        visitor_id: parseInt(visitorId, 10),
        time_out: checkoutTime,
        status: 'completed',
        // Add time_in if it was missing
        ...(updatedVisitor.time_in !== visitorToCheckout.time_in && {
          time_in: updatedVisitor.time_in
        })
      };

      // Rest of the function remains the same...

      // Get the hostname and port from the current window location
      const protocol = window.location.protocol;
      const hostname = window.location.hostname;
      const port = window.location.port ? window.location.port : '';
      const baseUrl = `${protocol}//${hostname}${port ? ':' + port : ''}`;

      let apiSuccess = false;
      let errorMessages = []; // Change to array to collect all error messages

      // Try to use the new guard-specific endpoint
      try {
        // Remove debug log
        const guardResponse = await axios.post(`${baseUrl}/api/guard/visitor/checkout`, checkoutData, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        });

        if (guardResponse.data && guardResponse.data.success) {
          // Remove debug log
          apiSuccess = true;
          toast.success('Visitor checked out successfully!');
          localStorage.setItem(ACTIVE_TAB_STORAGE_KEY, 'active');
          window.location.reload();

          // Refresh visitors list after successful checkout
          // setTimeout(() => refreshVisitors(), 1000);
        }
      } catch (guardError) {
        errorMessages.push(guardError.message || 'Error using guard-specific checkout endpoint');

        // Try alternative endpoint if the first one failed
        try {
          const altData = {
            id: parseInt(visitorId, 10),
            time_out: checkoutTime,
            status: 'completed'
          };

          const altResponse = await axios.post(`${baseUrl}/api/guard/visitors/checkout`, altData, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`,
              'Content-Type': 'application/json'
            }
          });

          if (altResponse.data && altResponse.data.success) {
            apiSuccess = true;
            toast.success('Visitor checked out successfully!');
            localStorage.setItem(ACTIVE_TAB_STORAGE_KEY, 'active');
            window.location.reload();

            // Refresh visitors list after successful checkout
            // setTimeout(() => refreshVisitors(), 1000);
          }
        } catch (altError) {
          errorMessages.push(altError.message || 'Error using alternative endpoint');

          // Try the status-only endpoint as last resort
          try {
            const statusResponse = await api.put(`/api/visitors/${visitorId}/status`, {
              status: 'completed'
            });

            if (statusResponse.data && statusResponse.data.success) {
              apiSuccess = true;
              toast.success('Visitor checked out successfully!');
              localStorage.setItem(ACTIVE_TAB_STORAGE_KEY, 'active');
              window.location.reload();
              // Refresh visitors list after successful checkout
              // setTimeout(() => refreshVisitors(), 1000);
            }
          } catch (statusError) {
            errorMessages.push(statusError.message || 'Error using status endpoint');
          }
        }
      }

      // If all API attempts failed, store in localStorage as a fallback
      if (!apiSuccess) {
        try {
          // Get existing checkouts from localStorage
          const localCheckouts = JSON.parse(localStorage.getItem('visitor_checkouts') || '[]');

          // Add this checkout to the list
          localCheckouts.push({
            visitorId: parseInt(visitorId, 10),
            timeOut: checkoutTime,
            checkedOutAt: new Date().toISOString(),
            visitorName: visitorToCheckout?.visitor_name || 'Unknown',
            syncStatus: 'local-only'
          });

          // Save back to localStorage
          localStorage.setItem('visitor_checkouts', JSON.stringify(localCheckouts));

          // Show appropriate messages to the user
          toast.success('Visitor marked as checked out in your view.');
          toast.info('Note: Changes may not be synchronized with the server due to permission restrictions.');
        } catch (storageError) {
          // If even localStorage fails, at least we've updated the UI state
          toast.warning('Checkout recorded locally but could not be saved to the server.');
        }
      }

    } catch (err) {
      // const finalErrorMessage = errorMessages.length > 0 
      //   ? errorMessages.join('; ') 
      //   : (err.message || 'Unknown error');
      // setError(`Failed to check out visitor: ${finalErrorMessage}`);
      // toast.error(`Failed to check out visitor: ${finalErrorMessage}`);

      // Revert local state in case of error
      await refreshVisitors();
    } finally {
      setLoading(false);
    }
  };

  // Function to fetch a specific visitor's details including photos
  const fetchVisitorDetails = async (visitorId) => {
    try {
      console.log(`Fetching details for visitor ID: ${visitorId}`);

      // Get user's organization ID from session storage
      const user = JSON.parse(sessionStorage.getItem('user') || '{}');
      const organizationId = user.organization_id;

      if (!organizationId) {
        console.log('Organization ID not found, cannot fetch visitor details');
        return null;
      }

      // Use the search endpoint with organization context
      const response = await api.get('/api/visitors/search', {
        params: {
          organization_id: organizationId,
          visitor_ids: [visitorId],
          include_photos: true,
          limit: 1
        }
      });

      if (response.data && Array.isArray(response.data) && response.data.length > 0) {
        const visitorData = response.data[0];
        console.log('Received visitor data from server');

        // Process URLs with our safe image URL handler
        if (visitorData.photo_url) {
          // Add cache busting to avoid browser caching
          const timestamp = new Date().getTime();
          const processedUrl = getSafeImageUrl(visitorData.photo_url);
          const cacheBustedUrl = processedUrl.includes('?')
            ? `${processedUrl}&t=${timestamp}`
            : `${processedUrl}?t=${timestamp}`;

          visitorData.photo_url = cacheBustedUrl;
          console.log('Processed photo URL for visitor');
        }

        if (visitorData.id_document_url) {
          // Add cache busting to avoid browser caching
          const timestamp = new Date().getTime();
          const processedUrl = getSafeImageUrl(visitorData.id_document_url);
          const cacheBustedUrl = processedUrl.includes('?')
            ? `${processedUrl}&t=${timestamp}`
            : `${processedUrl}?t=${timestamp}`;

          visitorData.id_document_url = cacheBustedUrl;
          console.log('Processed ID document URL for visitor');
        }

        // Update local state with the visitor details
        setLocalVisitors(prev =>
          prev.map(v => v.id === parseInt(visitorId) ? { ...v, ...visitorData } : v)
        );

        return visitorData;
      }
      console.log('No visitor data found on server');
      return null;
    } catch (error) {
      console.log('Error fetching visitor details:', error.message);
      return null;
    }
  };

  // Helper function to handle successful check-in
  const handleCheckInSuccess = (visitor) => {
    // Mark this visitor as active in localStorage for persistence
    if (visitor.id) {
      saveVisitorAsActive(visitor.id);
      // console.log(`Marked visitor ${visitor.id} as active in localStorage during check-in`);
    }

    // Save images to localStorage for this visitor
    if (capturedImage && visitor.id) {
      saveImageToLocalStorage(visitor.id, 'photo', capturedImage);
    }

    if (capturedIdImage && visitor.id) {
      saveImageToLocalStorage(visitor.id, 'id_document', capturedIdImage);
    }

    // Try direct image save if we have captured images
    if ((capturedImage || capturedIdImage) && visitor.id) {
      console.log('Attempting direct image save after successful check-in');
      saveImagesDirectlyToServer(visitor.id, capturedImage, capturedIdImage)
        .then(success => {
          if (success) {
            console.log('Direct image save successful after check-in');
          } else {
            console.warn('Direct image save failed after check-in, but images are saved in localStorage');
          }
        })
        .catch(err => {
          console.error('Error in direct image save after check-in:', err);
        });
    }

    // IMPORTANT: Update localVisitors state immediately to show the newly checked-in visitor in the Active tab
    setLocalVisitors(prevVisitors => {
      // Check if visitor already exists in the list
      const visitorExists = prevVisitors.some(v => v.id === visitor.id);

      if (visitorExists) {
        // Update the existing visitor
        return prevVisitors.map(v => {
          if (v.id === visitor.id) {
            return {
              ...v,
              ...visitor,
              status: 'active',
              time_in: visitor.time_in || formatMySQLDateTime(new Date())
            };
          }
          return v;
        });
      } else {
        // Add the new visitor to the list
        return [
          {
            ...visitor,
            status: 'active',
            time_in: visitor.time_in || formatMySQLDateTime(new Date())
          },
          ...prevVisitors
        ];
      }
    });

    // Close modal
    setShowCheckInModal(false);

    // Set visitor to null and reset captured images
    setVisitorToCheckIn(null);
    setCapturedImage(null);
    setCapturedIdImage(null);

    // Show success message
    toast.success('Visitor checked in successfully!');

    // Switch to active tab to show the newly checked-in visitor
    setActiveTab('active');
    // Persist the active tab in localStorage
    localStorage.setItem(ACTIVE_TAB_STORAGE_KEY, 'active');
    window.location.reload();
    console.log('Switched to active tab to show checked-in visitor');

    // Force a refresh after check-in to ensure we have the latest data including photos
    setTimeout(async () => {
      try {
        console.log('Refreshing visitor data to ensure photos are up to date...');

        // First, try to fetch this specific visitor's details
        await fetchVisitorDetails(visitor.id);

        // Then refresh all visitors
        const refreshedData = await refreshVisitors();
        console.log('Refresh successful, visitor count:', refreshedData?.length || 'unknown');
      } catch (err) {
        console.error('Error refreshing data after check-in:', err);
      }
    }, 1000);
  };

  // Check if any features are limited by plan
  // const checkPlanLimitations = async () => {
  //   try {
  //     // Make a small test request without actual visitor data
  //     const testResult = await api.post('/api/visitors/test-plan-features', {
  //       test: true
  //     });

  //     if (testResult?.data?.plan_limited) {
  //       setPlanLimitations({
  //         photoFeatureDisabled: true
  //       });
  //     } else {
  //       setPlanLimitations({
  //         photoFeatureDisabled: false
  //       });
  //     }
  //   } catch (error) {
  //     // Don't throw error for plan limitations check
  //     console.warn('Plan limitations check failed:', error);
  //     // Default to features enabled if check fails
  //     setPlanLimitations({
  //       photoFeatureDisabled: false
  //     });
  //   }
  // };

  // Add this function to handle data refresh more reliably
  const handleDataRefresh = async () => {
    try {
      setLoading(true);
      setError(null);

      const user = JSON.parse(sessionStorage.getItem('user') || '{}');
      const organizationId = user.organization_id;

      if (!organizationId) {
        setError('Organization context not found. Please try logging out and back in.');
        setLoading(false);
        return;
      }

      // Common request headers
      const headers = {
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
        'Content-Type': 'application/json'
      };

      // Force localhost in development, ignore environment variable if it points to production
      const baseUrl = (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
        ? 'https://nivaas.app'
        : (process.env.REACT_APP_API_URL || 'https://nivaas.app');

      // Reload units if needed
      if (!unitsLoaded || localVillas.length === 0) {
        try {
          console.log('Refreshing units data...');
          const unitsResponse = await axios.get(`${baseUrl}/api/units`, { headers });
          console.log(unitsResponse, 'unitsResponse');

          if (unitsResponse?.data) {
            try {
              const processedUnits = Array.isArray(unitsResponse.data)
                ? unitsResponse.data.map(unit => processUnitData(unit || {}))
                : [];
              setLocalVillas(processedUnits);
              setUnitsLoaded(true);
              console.log('Units refreshed successfully:', processedUnits.length);
            } catch (processingError) {
              console.error('Error processing units data:', processingError);
            }
          }
        } catch (unitsError) {
          console.error('Error refreshing units:', unitsError);
        }
      }

      // Fetch active, approved, and upcoming visitors
      try {
        const [activeResponse, requestedResponse] = await Promise.all([
          axios.get(`${baseUrl}/api/visitors/search`, {
            params: {
              status: ['active', 'approved', 'upcoming'],
              include_organization: true,
              organization_id: organizationId,
              limit: 500
            },
            headers
          }),
          visitorAPI.getRequestedVisitors({
            page: requestedVisitorsPagination.currentPage
          })
        ]);

        const activeData = activeResponse?.data || {};
        const activeVisitors = Array.isArray(activeData) ? activeData :
          activeData?.visitors ? activeData.visitors : [];

        const requestedData = requestedResponse?.data || {};
        const requestedVisitorsData = Array.isArray(requestedData) ? requestedData :
          requestedData?.visitors ? requestedData.visitors : [];

        // Update pagination info for requested visitors
        if (requestedData.total !== undefined) {
          setRequestedVisitorsPagination(prev => ({
            ...prev,
            total: requestedData.total,
            totalPages: requestedData.totalPages || Math.ceil(requestedData.total / 20), // Use static limit of 20
            currentPage: requestedData.page || prev.currentPage,
            limit: requestedData.limit || 20 // Use limit from backend response
          }));
        }

        console.log('Refreshed active visitors:', activeVisitors.map(v => ({
          id: v.id || 'unknown',
          name: v.visitor_name || 'unnamed',
          status: v.status || 'unknown',
          time_in: v.time_in || null,
          time_out: v.time_out || null
        })));

        console.log('Refreshed requested visitors:', requestedVisitorsData.length);

        setLocalVisitors(activeVisitors);
        setRequestedVisitors(requestedVisitorsData);
        setError(null);
      } catch (visitorError) {
        console.error('Error refreshing visitors:', visitorError);
        setError('Failed to refresh visitor data. Please try again.');
      }

    } catch (err) {
      console.error('Error refreshing data:', err);
      setError('Failed to refresh data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Add this helper function after saveImageToLocalStorage and getImageFromLocalStorage but before other functions
  // const ensureProperImageFormat = (imageData) => {
  //   if (!imageData) return null;

  //   try {
  //     // Check if it's already a data URL
  //     if (typeof imageData === 'string' && imageData.startsWith('data:')) {
  //       return imageData;
  //     }

  //     // If it's a string but not a data URL, assume it's base64 data without prefix
  //     if (typeof imageData === 'string') {
  //       return `data:image/jpeg;base64,${imageData}`;
  //     }

  //     // If we reach here, the format is unknown, return null
  //     // Remove error log
  //     return null;
  //   } catch (error) {
  //     // Remove error log
  //     return null;
  //   }
  // };

  // Add a reset function to ensure clean state
  const resetForm = () => {
    setFormData({ ...initialFormState });
    setCapturedImage(null);
    setCapturedIdImage(null);
    setShowCamera(false);
    setShowIdCamera(false);
    // Reset phone validation state
    setPhoneError('');
    setIsPhoneValid(true);
    // Reset visitor suggestions
    setVisitorSuggestions([]);
    setShowSuggestions(false);
    setLoadingSuggestions(false);

    // Reset original URL fields
    setFormData(prev => ({ ...prev, original_photo_url: null, original_id_document_url: null }));
    if (videoRef.current) {
      stopCamera();
    }
    if (idVideoRef.current) {
      stopIdCamera();
    }

    // Refresh data after form reset
    setTimeout(() => {
      handleDataRefresh();
      refreshVisitors();
    }, 500);
  };

  // Remove debug log before render
  // console.log('About to render Visitors component UI with:', {...});

  // Custom styles for visitor row animation and hide states
  const visitorStyles = `
    .visitor-checked-out { 
      display: none !important; 
    }
    tr.fade-out {
      opacity: 0;
      transition: opacity 0.5s ease-out;
    }
    .active-visitor-row[data-checkout-status="checked-out"] {
      display: none !important;
    }
    
    /* Visitor suggestion styles */
    .visitor-suggestion-item {
      transition: background-color 0.2s ease;
    }
    
    .visitor-suggestion-item:hover {
      background-color: #f8f9fa !important;
    }
    
    .visitor-suggestion-item:last-child {
      border-bottom: none !important;
    }
  `;

  // Add this useEffect before the return statement, grouped with other useEffect hooks
  useEffect(() => {
    // Ensure visitors approved by the owner are assigned a `time_in` value
    if (localVisitors.length > 0) {
      const updatedVisitors = localVisitors.map(visitor => {
        if (visitor.status === 'approved' && visitor.approved_by && !visitor.time_in) {
          return {
            ...visitor,
            time_in: formatMySQLDateTime(new Date())
          };
        }
        return visitor;
      });

      // Only update if there are actually changes to make
      const hasChanges = updatedVisitors.some((visitor, index) =>
        visitor !== localVisitors[index]
      );

      if (hasChanges) {
        setLocalVisitors(updatedVisitors);
      }
    }
  }, [localVisitors]);

  // Updated conditional rendering for loading state
  if ((loading || dataLoading) && localVisitors.length === 0) {
    return (
      <div className="d-flex justify-content-center mt-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  // Add a safer image URL handling function
  const getSafeImageUrl = (url, fallbackText = 'No Image') => {
    if (!url) return createLocalPlaceholder(fallbackText);

    try {
      // If it's a data URL, return as is
      if (url.startsWith('data:image')) {
        return url;
      }

      // Process through our centralized getImageUrl function for consistent handling
      let processedUrl = getImageUrl(url);
      if (!processedUrl) return createLocalPlaceholder(fallbackText);

      // Add cache busting parameter to avoid browser caching issues
      const separator = processedUrl.includes('?') ? '&' : '?';
      return `${processedUrl}${separator}t=${new Date().getTime()}`;
    } catch (error) {
      console.log('Error processing image URL:', error);
      return createLocalPlaceholder(fallbackText);
    }
  };

  // Add a unified error handler for image loading
  const handleImageLoadError = (event, fallbackText = 'No Image', elem) => {
    try {
      const img = event.target;

      // Only handle the error once to prevent infinite loops
      if (img.hasAttribute('data-error-handled')) {
        return;
      }

      // Mark this image as having been error-handled
      img.setAttribute('data-error-handled', 'true');

      // Log the error but use console.log to avoid console clutter
      console.log(`Image load error for ${img.alt}:`, event);

      // Get the visitor ID from the URL or data attribute
      let visitorId = null;
      if (img.src) {
        // Try to get visitor ID from URL if possible
        try {
          const matches = img.src.match(/visitor_(\d+)_org_|id_doc_(\d+)_org_/);
          if (matches && (matches[1] || matches[2])) {
            visitorId = matches[1] || matches[2];
          }
        } catch (e) {
          console.log('Error extracting visitor ID from URL:', e);
        }
      }

      // If no visitor ID found and we have a data attribute, use that
      if (!visitorId && img.dataset && img.dataset.visitorId) {
        visitorId = img.dataset.visitorId;
      }

      // Before using placeholder, try several approaches to load the image
      let retrySuccessful = false;

      // Approach 1: Try with API URL prefix if not already present
      if (img.src && !img.src.startsWith('data:') && !img.src.includes('placeholder')) {
        try {
          // Force localhost in development, ignore environment variable if it points to production
          const apiBaseUrl = (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
            ? 'https://nivaas.app'
            : (process.env.REACT_APP_API_URL || 'https://nivaas.app');
          let simplifiedUrl = img.src;

          // If the URL is already absolute, extract just the path portion
          if (simplifiedUrl.startsWith('http')) {
            try {
              const urlObj = new URL(simplifiedUrl);
              simplifiedUrl = urlObj.pathname + urlObj.search;
            } catch (e) {
              console.log('Error parsing URL:', e);
            }
          }

          // If the URL doesn't start with /api/, prepend it
          if (!simplifiedUrl.startsWith('/api/')) {
            if (!simplifiedUrl.startsWith('/')) {
              simplifiedUrl = `/api/visitors/${simplifiedUrl}`;
            }
          }

          // Add cache-busting parameter
          const separator = simplifiedUrl.includes('?') ? '&' : '?';
          const timestamp = Date.now();
          simplifiedUrl = `${simplifiedUrl}${separator}t=${timestamp}`;

          // Add bypass parameter to skip auth for direct image access
          if (!simplifiedUrl.includes('bypass=')) {
            simplifiedUrl = `${simplifiedUrl}&bypass=true`;
          }

          // Create new URL with API base
          const newSrc = `${apiBaseUrl}${simplifiedUrl.startsWith('/') ? '' : '/'}${simplifiedUrl}`;
          console.log(`Retrying with simplified URL: ${newSrc}`);

          // Only set if different from current src to prevent loops
          if (newSrc !== img.src) {
            img.src = newSrc;
            retrySuccessful = true;
          }
        } catch (retryError) {
          console.log('First retry attempt failed:', retryError);
        }
      }

      // Approach 2: If first retry fails, try alternative approach using visitor ID
      if (!retrySuccessful && visitorId) {
        try {
          console.log('First retry attempt failed, trying alternative');

          // Force localhost in development, ignore environment variable if it points to production
          const apiBaseUrl = (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
            ? 'https://nivaas.app'
            : (process.env.REACT_APP_API_URL || 'https://nivaas.app');
          const isIdDoc = img.alt && img.alt.toLowerCase().includes('id') || img.src.includes('id-document') || img.src.includes('id_doc');
          const endpoint = isIdDoc ? 'id-document' : 'photo';
          const newSrc = `${apiBaseUrl}/api/visitors/${endpoint}/${visitorId}?bypass=true&t=${Date.now()}`;

          // Only set if different from current src
          if (newSrc !== img.src) {
            console.log(`Trying with visitor ID-based URL: ${newSrc}`);
            img.src = newSrc;
            retrySuccessful = true;
          }
        } catch (altRetryError) {
          console.log('Alternative retry failed:', altRetryError);
        }
      }

      // Approach 3: If we have local storage backup, use it
      if (!retrySuccessful && visitorId) {
        try {
          console.log('Trying to use localStorage backup image for visitor', visitorId);
          const imageType = img.alt && img.alt.toLowerCase().includes('id') ? 'id_document' : 'photo';
          const storedImage = getImageFromLocalStorage(visitorId, imageType);

          if (storedImage) {
            console.log('Found image in localStorage, using it as fallback');
            img.src = storedImage;
            retrySuccessful = true;
          } else {
            console.log('No backup image found in localStorage');
          }
        } catch (lsError) {
          console.log('Error retrieving from localStorage:', lsError);
        }
      }

      // If all retries failed, use placeholder
      if (!retrySuccessful) {
        console.log('No visitor ID available, using placeholder');

        // Show placeholder image or text
        if (elem && typeof elem.setAttribute === 'function') {
          // Handle using custom element if provided
          elem.setAttribute('src', createLocalPlaceholder(fallbackText));
        } else {
          // Otherwise fallback to the image element itself
          img.src = createLocalPlaceholder(fallbackText);
        }
      }
    } catch (error) {
      console.log('Error in handleImageLoadError:', error);

      // Last resort fallback
      try {
        const img = event.target;
        img.src = createLocalPlaceholder('No Image');
      } catch (e) {
        // Silent catch
      }
    }
  };

  const selectedUnit = localVillas.find(
    villa => villa.id?.toString() === formData.unit_id?.toString()
  );
  const selectedUnitApprovalRequired = selectedUnit?.visitor_approval_required;

  function formatTimeDuration(duration) {
    if (!duration || isNaN(duration)) return 'Not specified';

    const totalMinutes = Math.round(duration * 60);
    const totalDays = Math.floor(totalMinutes / (24 * 60));

    let remainingMinutes = totalMinutes;

    let months = Math.floor(totalDays / 30);
    remainingMinutes -= months * 30 * 24 * 60;

    let weeks = Math.floor((remainingMinutes / (7 * 24 * 60)));
    remainingMinutes -= weeks * 7 * 24 * 60;

    let days = Math.floor(remainingMinutes / (24 * 60));
    remainingMinutes -= days * 24 * 60;

    let hours = Math.floor(remainingMinutes / 60);
    let minutes = remainingMinutes % 60;

    let parts = [];
    if (months > 0) parts.push(`${months} month${months > 1 ? 's' : ''}`);
    if (weeks > 0) parts.push(`${weeks} week${weeks > 1 ? 's' : ''}`);
    if (days > 0) parts.push(`${days} day${days > 1 ? 's' : ''}`);
    if (hours > 0) parts.push(`${hours} hour${hours > 1 ? 's' : ''}`);
    if (minutes > 0) parts.push(`${minutes} minute${minutes > 1 ? 's' : ''}`);

    return parts.length > 0 ? parts.join(' ') : '0 minutes';
  }

  return (
    <div className="container-fluid py-4">
      {/* Add custom CSS rules for visitor row states */}
      <style>{visitorStyles}</style>

      {/* Toast notifications container */}
      <ToastContainer position="top-right" autoClose={3000} />

      <PageTitle
        icon="fa-users"
        title="Visitor Management"
        action={
          <button
            className="btn btn-outline-primary"
            onClick={handleManualRefresh}
            disabled={loading}
          >
            <i className={`fas fa-sync-alt ${loading ? 'fa-spin' : ''} me-2`}></i>
            Refresh Data
          </button>
        }
      />

      {!unitsLoaded && (
        <div className="alert alert-warning mb-4">
          <i className="fas fa-exclamation-triangle me-2"></i>
          <strong>Failed to load units.</strong> Please refresh the page or click the button below.
          <div className="mt-2">
            <button
              className="btn btn-sm btn-warning"
              onClick={handleManualRefresh}
              disabled={loading}
            >
              <i className={`fas fa-sync-alt ${loading ? 'fa-spin' : ''} me-2`}></i>
              Retry Loading Units
            </button>
          </div>
        </div>
      )}

      {connectionError && (
        <div className="alert alert-danger mb-4">
          <i className="fas fa-exclamation-triangle me-2"></i>
          <strong>Connection Error: </strong>
          {connectionError}
        </div>
      )}

      {error && !connectionError && (
        <div className="alert alert-warning mb-4">
          <i className="fas fa-exclamation-circle me-2"></i>
          {error}
        </div>
      )}

      {showSuccess && checkedInVisitor && (
        <div className="alert alert-success alert-dismissible fade show mb-4" role="alert">
          <i className="fas fa-check-circle me-2"></i>
          <strong>Success!</strong> Visitor {checkedInVisitor.visitor_name} has been checked in to Unit {checkedInVisitor.villa_number}.
          <button
            type="button"
            className="btn-close"
            onClick={() => setShowSuccess(false)}
            aria-label="Close"
          ></button>
        </div>
      )}

      {/* Tabs for different functions */}
      <ul className="nav nav-tabs mb-4">
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === 'check-in' ? 'active' : ''}`}
            onClick={() => {
              handleTabChange('check-in');
            }}
          >
            <i className="fas fa-sign-in-alt me-2"></i>
            Visitor Check-In
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === 'check-out' ? 'active' : ''}`}
            onClick={() => {
              handleTabChange('check-out');
            }}
          >
            <i className="fas fa-sign-out-alt me-2"></i>
            Check-Out
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === 'active' ? 'active' : ''}`}
            onClick={() => {
              handleTabChange('active');
            }}
          >
            <i className="fas fa-users me-2"></i>
            Active Visitors <span className="badge bg-danger ms-1">{getActiveVisitors().length}</span>
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === 'daily-visitors' ? 'active' : ''}`}
            onClick={() => {
              handleTabChange('daily-visitors');
            }}
          >
            <i className="fas fa-calendar-day me-2"></i>
            Daily Visitors <span className="badge bg-primary ms-1">{dailyVisitorsPagination.total}</span>
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === 'requested' ? 'active' : ''}`}
            onClick={() => {
              handleTabChange('requested');
            }}
          >
            <i className="fas fa-user-clock me-2"></i>
            Requested Visitors <span className="badge bg-warning ms-1">{requestedVisitorsPagination.total}</span>
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === 'upcoming' ? 'active' : ''}`}
            onClick={() => {
              handleTabChange('upcoming');
            }}
          >
            <i className="fas fa-clock me-2"></i>
            Upcoming Visitors <span className="badge bg-info ms-1">{getUpcomingVisitors().length}</span>
          </button>
        </li>
      </ul>

      {/* Conditional rendering based on activeTab */}
      {activeTab === 'check-in' && (
        <div className="row">
          <div className="col-lg-8">
            <div className="card mb-4">
              <div className="card-header">
                <h5 className="mb-0">New Visitor Check-In</h5>
              </div>
              <div className="card-body">
                <form onSubmit={handleCheckIn}>
                  <div className="row mb-3">
                    <div className="col-md-6">
                      <label htmlFor="visitor_name" className="form-label">Visitor Name <span className="text-danger">*</span></label>
                      <input
                        type="text"
                        id="visitor_name"
                        name="visitor_name"
                        className="form-control"
                        placeholder="Enter visitor's full name"
                        value={formData.visitor_name}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                    <div className="col-md-6">
                      <label htmlFor="unit_id" className="form-label">Unit <span className="text-danger">*</span></label>
                      <select
                        id="unit_id"
                        name="unit_id"
                        className="form-select"
                        value={formData.unit_id}
                        onChange={handleInputChange}
                        required
                        disabled={loading || !localVillas || localVillas.length === 0}
                      >
                        <option value="">Select Unit</option>
                        {localVillas && localVillas.length > 0 ? (
                          localVillas.map(villa => (
                            <option key={villa.id} value={villa.id}>
                              {villa.villa_number}
                            </option>
                          ))
                        ) : (
                          <option value="" disabled>Loading units...</option>
                        )}
                      </select>
                      {loading && (
                        <div className="text-muted small mt-1">
                          <i className="fas fa-spinner fa-spin me-1"></i>
                          Loading units...
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="row mb-3">
                    <div className="col-md-6">
                      <label htmlFor="purpose" className="form-label">Purpose of Visit <span className="text-danger">*</span></label>
                      <input
                        type="text"
                        id="purpose"
                        name="purpose"
                        className="form-control"
                        placeholder="e.g., Delivery, Meeting, Maintenance"
                        value={formData.purpose}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                    <div className="col-md-6">
                      <label htmlFor="phone" className="form-label">Phone Number</label>
                      <div className="position-relative" ref={phoneInputRef}>
                        <input
                          type="tel"
                          id="phone"
                          name="phone"
                          className={`form-control ${phoneError ? 'is-invalid' : isPhoneValid && formData.phone ? 'is-valid' : ''}`}
                          placeholder="10-digit mobile number"
                          value={formData.phone}
                          onChange={handleInputChange}
                          inputMode="numeric"
                          pattern="[0-9]*"
                          maxLength="10"
                          autoComplete="off"
                        />

                        {/* Visitor Suggestions Dropdown */}
                        {showSuggestions && (
                          <div className="position-absolute w-100 bg-white border rounded shadow-lg" style={{ zIndex: 1050, top: '100%', maxHeight: '300px', overflowY: 'auto' }}>
                            {loadingSuggestions ? (
                              <div className="p-3 text-center text-muted">
                                <div className="spinner-border spinner-border-sm me-2" role="status"></div>
                                Loading suggestions...
                              </div>
                            ) : visitorSuggestions.length > 0 ? (
                              <>
                                <div className="p-2 border-bottom bg-light">
                                  <small className="text-muted">
                                    <i className="fas fa-info-circle me-1"></i>
                                    Previous visitors with this number - Click to auto-fill
                                  </small>
                                </div>
                                {visitorSuggestions.map((visitor, index) => (
                                  <div
                                    key={visitor.id}
                                    className="p-3 border-bottom visitor-suggestion-item"
                                    style={{ cursor: 'pointer' }}
                                    onClick={() => handleVisitorSelection(visitor)}
                                    onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                                    onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                                  >
                                    <div className="d-flex align-items-center">
                                      <div className="flex-grow-1">
                                        <div className="fw-bold text-primary">{visitor.visitor_name}</div>
                                        <div className="text-muted small">
                                          <i className="fas fa-phone me-1"></i>
                                          {visitor.phone}
                                        </div>
                                        <div className="text-muted small">
                                          <i className="fas fa-home me-1"></i>
                                          {visitor.villa_number || 'Unit not specified'}
                                        </div>
                                        {visitor.purpose && (
                                          <div className="text-muted small">
                                            <i className="fas fa-info-circle me-1"></i>
                                            {visitor.purpose}
                                          </div>
                                        )}
                                        <div className="text-muted small">
                                          <i className="fas fa-calendar me-1"></i>
                                          Last visit: {new Date(visitor.created_at).toLocaleDateString()}
                                        </div>
                                      </div>
                                      <div className="ms-2">
                                        <i className="fas fa-chevron-right text-muted"></i>
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </>
                            ) : (
                              <div className="p-3 text-center text-muted">
                                No previous visitors found with this number
                              </div>
                            )}
                          </div>
                        )}
                      </div>

                      {phoneError && (
                        <div className="invalid-feedback">
                          {phoneError}
                        </div>
                      )}
                      {!phoneError && formData.phone && (
                        <div className="valid-feedback">
                          Valid mobile number
                        </div>
                      )}
                      <small className="form-text text-muted">
                        Enter 10-digit Indian mobile number (starting with 6, 7, 8, or 9)
                      </small>
                    </div>
                  </div>

                  <div className="row mb-3">
                    <div className="col-md-6">
                      <label htmlFor="expected_duration" className="form-label">Expected Duration</label>
                      <select
                        id="expected_duration"
                        name="expected_duration"
                        className="form-select"
                        value={formData.expected_duration}
                        onChange={handleInputChange}
                      >
                        <option value="0.5">{formatDuration(0.5)}</option>
                        <option value="1">{formatDuration(1)}</option>
                        <option value="2">{formatDuration(2)}</option>
                        <option value="3">{formatDuration(3)}</option>
                        <option value="4">{formatDuration(4)}</option>
                        <option value="8">{formatDuration(8)}</option>
                        <option value="24">{formatDuration(24)}</option>
                        <option value="48">{formatDuration(48)}</option>
                        <option value="72">{formatDuration(72)}</option>
                        <option value="168">{formatDuration(168)}</option>
                        <option value="720">{formatDuration(720)}</option>
                      </select>
                    </div>
                    <div className="col-md-6">
                      <div className="form-check mt-4">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          id="requires_approval"
                          name="requires_approval"
                          checked={formData.requires_approval === true}
                          disabled={selectedUnitApprovalRequired === 1}
                          onChange={(e) => {
                            const newValue = e.target.checked;
                            console.log('Requires approval changed to:', newValue);
                            setFormData(prev => ({
                              ...prev,
                              requires_approval: newValue
                            }));
                          }}
                        />
                        <label className="form-check-label" htmlFor="requires_approval">
                          Requires Owner Approval
                        </label>
                        <small className="form-text text-muted d-block">
                          {formData.requires_approval === true ?
                            'Owner approval will be required before visitor can enter' :
                            'Visitor will be directly checked in without owner approval'}
                        </small>
                      </div>
                    </div>
                  </div>

                  {/* Visitor Photo Capture */}
                  <div className="mb-4">
                    <label className="form-label d-block">Visitor Photo</label>

                    {/* <PlanFeatureNotice feature="Photo capture" planName="Free" /> */}

                    {!capturedImage && !showCamera && (
                      <div className="d-flex gap-2 mb-3">
                        <button
                          type="button"
                          className="btn btn-outline-primary"
                          onClick={() => setShowCamera(true)}
                        >
                          <i className="fas fa-camera me-2"></i>
                          Capture Photo
                        </button>
                        <button
                          type="button"
                          className="btn btn-outline-secondary"
                          onClick={() => setShowFileUploadFallback(true)}
                        >
                          <i className="fas fa-upload me-2"></i>
                          Upload Image
                        </button>
                      </div>
                    )}

                    {showFileUploadFallback && (
                      <div className="mb-3">
                        <input
                          type="file"
                          className="form-control"
                          accept="image/*"
                          ref={fileInputRef}
                          onChange={handleFileUpload}
                        />
                      </div>
                    )}

                    {showCamera && (
                      <div className="card mb-3" style={{ position: 'relative', zIndex: 1000 }}>
                        <div className="card-body">
                          {cameraLoading && (
                            <div className="text-center my-3">
                              <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Loading camera...</span>
                              </div>
                              <p className="mt-2">Accessing camera...</p>
                            </div>
                          )}

                          {cameraError && (
                            <div className="alert alert-danger">
                              <i className="fas fa-exclamation-circle me-2"></i>
                              {cameraError}
                              <div className="mt-2">
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-danger me-2"
                                  onClick={() => {
                                    setShowCamera(false);
                                    setCameraError(null);
                                  }}
                                >
                                  Cancel
                                </button>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-primary"
                                  onClick={() => setShowFileUploadFallback(true)}
                                >
                                  Upload Instead
                                </button>
                              </div>
                            </div>
                          )}

                          <div className="text-center" style={{ position: 'relative', minHeight: '240px' }}>
                            <div className="camera-debug-info small text-muted mb-2">
                              Camera Status: {cameraLoading ? 'Loading' : cameraError ? 'Error' : cameraStream ? 'Active' : 'Inactive'}
                            </div>
                            <video
                              ref={videoRef}
                              width="320"
                              height="240"
                              autoPlay
                              playsInline
                              muted
                              style={{
                                display: !cameraLoading && !cameraError ? 'block' : 'none',
                                margin: '0 auto',
                                border: `2px solid ${primaryColor}`,
                                backgroundColor: '#f8f9fa',
                                maxWidth: '100%',
                                boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
                              }}
                            ></video>
                            <canvas
                              ref={canvasRef}
                              width="320"
                              height="240"
                              style={{ display: 'none' }}
                            ></canvas>

                            {!cameraError && cameraStream && !videoRef.current?.srcObject && (
                              <div className="alert alert-warning mt-2">
                                <i className="fas fa-exclamation-triangle me-2"></i>
                                Camera stream exists but is not attached to video element. Refreshing may help.
                              </div>
                            )}
                          </div>

                          {!cameraError && !cameraLoading && (
                            <div className="text-center mt-3">
                              <button
                                type="button"
                                className="btn btn-primary me-2"
                                onClick={capturePhoto}
                              >
                                <i className="fas fa-camera me-2"></i>
                                Capture
                              </button>
                              <button
                                type="button"
                                className="btn btn-outline-secondary"
                                onClick={() => {
                                  stopCamera();
                                  setShowCamera(false);
                                }}
                              >
                                Cancel
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {capturedImage && (
                      <div className="mb-3">
                        <div className="position-relative d-inline-block">
                          <img
                            src={capturedImage}
                            alt="Captured"
                            className="img-thumbnail"
                            style={{ maxHeight: '200px' }}
                          />
                          <button
                            type="button"
                            className="btn btn-sm btn-danger position-absolute top-0 end-0 m-1"
                            onClick={() => {
                              setCapturedImage(null);
                              // Clear original photo URL when image is removed
                              setFormData(prev => ({ ...prev, original_photo_url: null }));
                            }}
                          >
                            <i className="fas fa-times"></i>
                          </button>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* ID Document Photo Capture */}
                  <div className="mb-4">
                    <label className="form-label d-block">ID Document</label>

                    {!capturedIdImage && !showIdCamera && (
                      <div className="d-flex gap-2 mb-3">
                        <button
                          type="button"
                          className="btn btn-outline-primary"
                          onClick={() => setShowIdCamera(true)}
                        >
                          <i className="fas fa-id-card me-2"></i>
                          Capture ID
                        </button>
                        <button
                          type="button"
                          className="btn btn-outline-secondary"
                          onClick={() => setShowIdFileUploadFallback(true)}
                        >
                          <i className="fas fa-upload me-2"></i>
                          Upload ID Image
                        </button>
                      </div>
                    )}

                    {showIdFileUploadFallback && (
                      <div className="mb-3">
                        <input
                          type="file"
                          className="form-control"
                          accept="image/*"
                          ref={idFileInputRef}
                          onChange={handleIdFileUpload}
                        />
                      </div>
                    )}

                    {showIdCamera && (
                      <div className="card mb-3" style={{ position: 'relative', zIndex: 1000 }}>
                        <div className="card-body">
                          {idCameraLoading && (
                            <div className="text-center my-3">
                              <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Loading camera...</span>
                              </div>
                              <p className="mt-2">Accessing camera...</p>
                            </div>
                          )}

                          {idCameraError && (
                            <div className="alert alert-danger">
                              <i className="fas fa-exclamation-circle me-2"></i>
                              {idCameraError}
                              <div className="mt-2">
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-danger me-2"
                                  onClick={() => {
                                    setShowIdCamera(false);
                                    setIdCameraError(null);
                                  }}
                                >
                                  Cancel
                                </button>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-primary"
                                  onClick={() => setShowIdFileUploadFallback(true)}
                                >
                                  Upload Instead
                                </button>
                              </div>
                            </div>
                          )}

                          <div className="text-center" style={{ position: 'relative', minHeight: '240px' }}>
                            <div className="camera-debug-info small text-muted mb-2">
                              ID Camera Status: {idCameraLoading ? 'Loading' : idCameraError ? 'Error' : idCameraStream ? 'Active' : 'Inactive'}
                            </div>
                            <video
                              ref={idVideoRef}
                              width="320"
                              height="240"
                              autoPlay
                              playsInline
                              muted
                              style={{
                                display: !idCameraLoading && !idCameraError ? 'block' : 'none',
                                margin: '0 auto',
                                border: `2px solid ${primaryColor}`,
                                backgroundColor: '#f8f9fa',
                                maxWidth: '100%',
                                boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
                              }}
                            ></video>
                            <canvas
                              ref={idCanvasRef}
                              width="320"
                              height="240"
                              style={{ display: 'none' }}
                            ></canvas>

                            {!idCameraError && idCameraStream && !idVideoRef.current?.srcObject && (
                              <div className="alert alert-warning mt-2">
                                <i className="fas fa-exclamation-triangle me-2"></i>
                                ID Camera stream exists but is not attached to video element. Refreshing may help.
                              </div>
                            )}
                          </div>

                          {!idCameraError && !idCameraLoading && (
                            <div className="text-center mt-3">
                              <button
                                type="button"
                                className="btn btn-primary me-2"
                                onClick={captureIdPhoto}
                              >
                                <i className="fas fa-camera me-2"></i>
                                Capture ID
                              </button>
                              <button
                                type="button"
                                className="btn btn-outline-secondary"
                                onClick={() => {
                                  stopIdCamera();
                                  setShowIdCamera(false);
                                }}
                              >
                                Cancel
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {capturedIdImage && (
                      <div className="mb-3">
                        <div className="position-relative d-inline-block">
                          <img
                            src={capturedIdImage}
                            alt="ID Document"
                            className="img-thumbnail"
                            style={{ maxHeight: '200px' }}
                          />
                          <button
                            type="button"
                            className="btn btn-sm btn-danger position-absolute top-0 end-0 m-1"
                            onClick={() => {
                              setCapturedIdImage(null);
                              // Clear original ID document URL when image is removed
                              setFormData(prev => ({ ...prev, original_id_document_url: null }));
                            }}
                          >
                            <i className="fas fa-times"></i>
                          </button>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="text-center mt-4">
                    <button
                      type="submit"
                      className="btn btn-primary btn-lg"
                      disabled={loading || !formData.visitor_name || !formData.unit_id || !formData.purpose}
                      onClick={handleSubmitButtonClick}
                    >
                      {loading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                          Processing...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-check-circle me-2"></i>
                          Complete Check-In
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>

          <div className="col-lg-4">
            <div className="card">
              <div className="card-header bg-info text-white">
                <h5 className="mb-0">Today&apos;s Expected Visitors</h5>
                <p className="mb-0 small">Check-in scheduled visitors for today</p>
              </div>
              <div className="card-body p-0">
                {(() => {
                  // Get today's visitors
                  const todayVisitors = getTodayExpectedVisitors();

                  // If no visitors for today, check if there are any upcoming visitors at all
                  if (todayVisitors.length === 0) {
                    const allUpcoming = getApprovedVisitors();

                    // Debug - log all upcoming visitors to console
                    if (allUpcoming.length > 0) {
                      console.log('No visitors found for today, but found upcoming visitors:',
                        allUpcoming.map(v => ({
                          id: v.id,
                          name: v.visitor_name,
                          expected_date: v.expected_date,
                          status: v.status
                        }))
                      );
                    }

                    return (
                      <div className="p-4 text-center">
                        <i className="fas fa-info-circle me-2"></i>
                        No visitors scheduled for today
                      </div>
                    );
                  }

                  // Display today's visitors
                  return (
                    <div className="list-group list-group-flush">
                      {todayVisitors.map(visitor => (
                        <div key={visitor.id} className="list-group-item">
                          <div className="d-flex justify-content-between align-items-center">
                            <div>
                              <h6 className="mb-1">{visitor.visitor_name}</h6>
                              <p className="mb-1 small">
                                <i className="fas fa-home me-1"></i> Unit {visitor.villa_number}
                              </p>
                              <p className="mb-0 small text-muted">
                                <i className="fas fa-info-circle me-1"></i>
                                {visitor.purpose}
                              </p>
                              <p className="mb-0 small text-muted">
                                <i className="fas fa-clock me-1"></i>
                                Expected at: {visitor.expected_time}
                              </p>
                            </div>
                            <button
                              className="btn btn-sm btn-primary"
                              onClick={() => {
                                // Get the current visitor directly
                                setVisitorToCheckIn(visitor);
                                setShowCheckInModal(true);
                                setCapturedImage(null);
                                setCapturedIdImage(null);
                              }}
                            >
                              <i className="fas fa-sign-in-alt me-1"></i> Check-In
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Check-in modal for upcoming visitors */}
      {showCheckInModal && visitorToCheckIn && (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header" style={{ backgroundColor: settings.block_title_bg_color || '#f8f9fa', color: settings.block_title_color || '#8B4513' }}>
                <h5 className="modal-title">Check-In Visitor: {visitorToCheckIn.visitor_name}</h5>
                <button type="button" className="btn-close"
                  onClick={() => {
                    setShowCheckInModal(false);
                    setVisitorToCheckIn(null);
                    setCapturedImage(null);
                    setCapturedIdImage(null);
                    stopCamera();
                    stopIdCamera();
                  }}
                  style={{ backgroundImage: closeIconUrl, fontSize: '18px', opacity: '0.9' }}
                ></button>
              </div>
              <div className="modal-body">
                <div className="row mb-3">
                  <div className="col-md-6">
                    <p><strong>Name:</strong> {visitorToCheckIn.visitor_name}</p>
                    <p><strong>Unit:</strong> {visitorToCheckIn.villa_number}</p>
                    <p><strong>Purpose:</strong> {visitorToCheckIn.purpose}</p>
                  </div>
                  <div className="col-md-6">
                    <p><strong>Expected Date:</strong> {visitorToCheckIn.expected_date || 'Not specified'}</p>
                    <p><strong>Expected Time:</strong> {visitorToCheckIn.expected_time || 'Not specified'}</p>
                    <p><strong>Phone:</strong> {visitorToCheckIn.phone || 'Not provided'}</p>
                  </div>
                </div>

                {/* Visitor Photo Capture */}
                <div className="mb-4">
                  <label className="form-label d-block">Visitor Photo</label>

                  {/* <PlanFeatureNotice feature="Photo capture" planName="Free" /> */}

                  {!capturedImage && !showCamera && (
                    <div className="d-flex gap-2 mb-3">
                      <button
                        type="button"
                        className="btn btn-outline-primary"
                        onClick={() => setShowCamera(true)}
                      >
                        <i className="fas fa-camera me-2"></i>
                        Capture Photo
                      </button>
                      <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={() => setShowFileUploadFallback(true)}
                      >
                        <i className="fas fa-upload me-2"></i>
                        Upload Image
                      </button>
                    </div>
                  )}

                  {showFileUploadFallback && (
                    <div className="mb-3">
                      <input
                        type="file"
                        className="form-control"
                        accept="image/*"
                        ref={fileInputRef}
                        onChange={handleFileUpload}
                      />
                    </div>
                  )}

                  {showCamera && (
                    <div className="card mb-3">
                      <div className="card-body">
                        {cameraLoading && (
                          <div className="text-center my-3">
                            <div className="spinner-border text-primary" role="status">
                              <span className="visually-hidden">Loading camera...</span>
                            </div>
                            <p className="mt-2">Accessing camera...</p>
                          </div>
                        )}

                        {cameraError && (
                          <div className="alert alert-danger">
                            <i className="fas fa-exclamation-circle me-2"></i>
                            {cameraError}
                            <div className="mt-2">
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-danger me-2"
                                onClick={() => {
                                  setShowCamera(false);
                                  setCameraError(null);
                                }}
                              >
                                Cancel
                              </button>
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-primary"
                                onClick={() => setShowFileUploadFallback(true)}
                              >
                                Upload Instead
                              </button>
                            </div>
                          </div>
                        )}

                        <div className="text-center">
                          <video
                            ref={videoRef}
                            width="320"
                            height="240"
                            autoPlay
                            playsInline
                            muted
                            style={{
                              display: !cameraLoading && !cameraError ? 'block' : 'none',
                              margin: '0 auto',
                              border: '2px solid #0d6efd',
                              backgroundColor: '#f8f9fa'
                            }}
                          ></video>
                          <canvas
                            ref={canvasRef}
                            width="320"
                            height="240"
                            style={{ display: 'none' }}
                          ></canvas>
                        </div>

                        {!cameraError && !cameraLoading && (
                          <div className="text-center mt-3">
                            <button
                              type="button"
                              className="btn btn-primary me-2"
                              onClick={capturePhoto}
                            >
                              <i className="fas fa-camera me-2"></i>
                              Capture
                            </button>
                            <button
                              type="button"
                              className="btn btn-outline-secondary"
                              onClick={() => {
                                stopCamera();
                                setShowCamera(false);
                              }}
                            >
                              Cancel
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {capturedImage && (
                    <div className="mb-3">
                      <div className="position-relative d-inline-block">
                        <img
                          src={capturedImage}
                          alt="Captured"
                          className="img-thumbnail"
                          style={{ maxHeight: '200px' }}
                        />
                        <button
                          type="button"
                          className="btn btn-sm btn-danger position-absolute top-0 end-0 m-1"
                          onClick={() => {
                            setCapturedImage(null);
                            // Clear original photo URL when image is removed
                            setFormData(prev => ({ ...prev, original_photo_url: null }));
                          }}
                        >
                          <i className="fas fa-times"></i>
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {/* ID Document Photo Capture */}
                <div className="mb-4">
                  <label className="form-label d-block">ID Document</label>

                  {/* <PlanFeatureNotice feature="ID document" planName="Free" /> */}

                  {!capturedIdImage && !showIdCamera && (
                    <div className="d-flex gap-2 mb-3">
                      <button
                        type="button"
                        className="btn btn-outline-primary"
                        onClick={() => setShowIdCamera(true)}
                      >
                        <i className="fas fa-id-card me-2"></i>
                        Capture ID
                      </button>
                      <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={() => setShowIdFileUploadFallback(true)}
                      >
                        <i className="fas fa-upload me-2"></i>
                        Upload ID Image
                      </button>
                    </div>
                  )}

                  {showIdFileUploadFallback && (
                    <div className="mb-3">
                      <input
                        type="file"
                        className="form-control"
                        accept="image/*"
                        ref={idFileInputRef}
                        onChange={handleIdFileUpload}
                      />
                    </div>
                  )}

                  {showIdCamera && (
                    <div className="card mb-3">
                      <div className="card-body">
                        {idCameraLoading && (
                          <div className="text-center my-3">
                            <div className="spinner-border text-primary" role="status">
                              <span className="visually-hidden">Loading camera...</span>
                            </div>
                            <p className="mt-2">Accessing camera...</p>
                          </div>
                        )}

                        {idCameraError && (
                          <div className="alert alert-danger">
                            <i className="fas fa-exclamation-circle me-2"></i>
                            {idCameraError}
                            <div className="mt-2">
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-danger me-2"
                                onClick={() => {
                                  setShowIdCamera(false);
                                  setIdCameraError(null);
                                }}
                              >
                                Cancel
                              </button>
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-primary"
                                onClick={() => setShowIdFileUploadFallback(true)}
                              >
                                Upload Instead
                              </button>
                            </div>
                          </div>
                        )}

                        <div className="text-center">
                          <video
                            ref={idVideoRef}
                            width="320"
                            height="240"
                            autoPlay
                            playsInline
                            muted
                            style={{
                              display: !idCameraLoading && !idCameraError ? 'block' : 'none',
                              margin: '0 auto',
                              border: '2px solid #0d6efd',
                              backgroundColor: '#f8f9fa'
                            }}
                          ></video>
                          <canvas
                            ref={idCanvasRef}
                            width="320"
                            height="240"
                            style={{ display: 'none' }}
                          ></canvas>
                        </div>

                        {!idCameraError && !idCameraLoading && (
                          <div className="text-center mt-3">
                            <button
                              type="button"
                              className="btn btn-primary me-2"
                              onClick={captureIdPhoto}
                            >
                              <i className="fas fa-camera me-2"></i>
                              Capture ID
                            </button>
                            <button
                              type="button"
                              className="btn btn-outline-secondary"
                              onClick={() => {
                                stopIdCamera();
                                setShowIdCamera(false);
                              }}
                            >
                              Cancel
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {capturedIdImage && (
                    <div className="mb-3">
                      <div className="position-relative d-inline-block">
                        <img
                          src={capturedIdImage}
                          alt="ID Document"
                          className="img-thumbnail"
                          style={{ maxHeight: '200px' }}
                        />
                        <button
                          type="button"
                          className="btn btn-sm btn-danger position-absolute top-0 end-0 m-1"
                          onClick={() => {
                            setCapturedIdImage(null);
                            // Clear original ID document URL when image is removed
                            setFormData(prev => ({ ...prev, original_id_document_url: null }));
                          }}
                        >
                          <i className="fas fa-times"></i>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowCheckInModal(false);
                    setVisitorToCheckIn(null);
                    setCapturedImage(null);
                    setCapturedIdImage(null);
                    stopCamera();
                    stopIdCamera();
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={loading}
                  onClick={processVisitorCheckIn}
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Processing...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-check-circle me-2"></i>
                      Complete Check-In
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Check-out tab */}
      {activeTab === 'check-out' && (
        <div className="row">
          <div className="col-md-12">
            <div className="card">
              <div className="card-header">
                <h5 className="card-title mb-0">Check-out Visitor</h5>
              </div>
              <div className="card-body">
                <div className="row mb-4">
                  <div className="col-md-6">
                    <label htmlFor="checkoutId" className="form-label">Visitor ID <span className="text-danger">*</span></label>
                    <div className="input-group">
                      <input
                        type="text"
                        id="checkoutId"
                        className="form-control"
                        placeholder="Enter visitor ID"
                        value={checkoutId}
                        onChange={(e) => setCheckoutId(e.target.value)}
                      />
                      <button
                        className="btn btn-primary"
                        type="button"
                        onClick={() => handleCheckoutVisitor(checkoutId)}
                        disabled={loading || !checkoutId}
                      >
                        {loading ? (
                          <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                        ) : (
                          <i className="fas fa-sign-out-alt"></i>
                        )} Check-Out
                      </button>
                    </div>
                    <small className="text-muted">Enter the visitor ID to check them out</small>
                  </div>
                </div>

                <div className="mt-4">
                  <h6>Active Visitors Available for Check-Out</h6>
                  {getActiveVisitors().length === 0 ? (
                    <div className="alert alert-info">
                      <i className="fas fa-info-circle me-2"></i>
                      No active visitors to check out
                    </div>
                  ) : (
                    <div className="table-responsive">
                      <table className="table table-sm table-hover">
                        <thead>
                          <tr>
                            <th>ID</th>
                            <th>Visitor Name</th>
                            <th>Unit</th>
                            <th>Check-In Time</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {getActiveVisitors().map(visitor => (
                            <tr key={visitor.id}>
                              <td>{visitor.id}</td>
                              <td>{visitor.visitor_name}</td>
                              <td>{getVisitorUnitsLabel(visitor)}</td>
                              <td>{formatVisitorDateTime(visitor.time_in)}</td>
                              <td>
                                <button
                                  className="btn btn-sm btn-danger"
                                  onClick={() => {
                                    // Execute checkout directly with visitor ID instead of setting state first
                                    handleCheckoutVisitor(visitor.id.toString());
                                  }}
                                >
                                  <i className="fas fa-sign-out-alt"></i> Check-Out
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Active Visitors Tab */}
      {activeTab === 'active' && (
        <div className="row">
          <div className="col-md-12">
            <div className="card">
              <div className="card-header d-flex justify-content-between align-items-center">
                <h5 className="card-title mb-0">Active Visitors</h5>
                <span className="badge bg-danger">{getActiveVisitors().length}</span>
              </div>
              <div className="card-body">
                {getActiveVisitors().length === 0 ? (
                  <div className="text-center py-4">
                    <i className="fas fa-users fa-3x mb-3 text-muted"></i>
                    <p>No active visitors at the moment</p>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-hover">
                      <thead>
                        <tr>
                          <th>ID</th>
                          <th>Visitor Name</th>
                          <th>Unit</th>
                          <th>Purpose</th>
                          <th>Check-In Time</th>
                          <th>Duration</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {getActiveVisitors().map(visitor => (
                          <tr
                            key={visitor.id}
                            className="active-visitor-row"
                            data-visitor-id={visitor.id}
                            data-visitor-status={visitor.status}
                            data-checkout-status={visitor.time_out ? 'checked-out' : 'active'}
                          >
                            <td>{visitor.id}</td>
                            <td>{visitor.visitor_name}</td>
                            <td>{getVisitorUnitsLabel(visitor)}</td>
                            <td>{visitor.purpose}</td>
                            <td>{formatVisitorDateTime(visitor.time_in)}</td>
                            <td>
                              {visitor.time_in ? (
                                calculateDuration(visitor.time_in, null)
                              ) : (
                                'Not checked in'
                              )}
                            </td>
                            <td>
                              <div className="btn-group">
                                <button
                                  className="btn btn-sm btn-info me-1"
                                  onClick={() => handleShowDetails(visitor.id)}
                                >
                                  <i className="fas fa-eye"></i>
                                </button>
                                <button
                                  className="btn btn-sm btn-danger"
                                  onClick={() => {
                                    // Execute checkout directly with visitor ID instead of setting state first
                                    handleCheckoutVisitor(visitor.id.toString());
                                  }}
                                  disabled={visitor.time_out || visitor.status === 'completed'}
                                >
                                  <i className="fas fa-sign-out-alt"></i> Check-Out
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Daily Visitors Tab */}
      {activeTab === 'daily-visitors' && (
        <div className="row">
          <div className="col-md-12">
            <div className="card">
              <div className="card-header d-flex justify-content-between align-items-center">
                <h5 className="card-title mb-0">
                  <i className="fas fa-calendar-day me-2"></i>
                  Daily Visitors
                </h5>
                <button
                  className="btn btn-primary btn-sm"
                  onClick={openAddDailyVisitorModal}
                >
                  <i className="fas fa-plus me-1"></i>
                  Add Daily Visitor
                </button>
              </div>
              <div className="card-body">
                {/* Search Box */}
                <div className="row mb-3">
                  <div className="col-md-6">
                    <div className="input-group">
                      <span className="input-group-text">
                        <i className="fas fa-search"></i>
                      </span>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Search by name or contact..."
                        value={dailyVisitorsSearch}
                        onChange={handleDailyVisitorsSearch}
                      />
                    </div>
                  </div>
                </div>

                {dailyVisitorsLoading ? (
                  <div className="text-center py-4">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-2">Loading daily visitors...</p>
                  </div>
                ) : dailyVisitors.length === 0 ? (
                  <div className="text-center py-4">
                    <i className="fas fa-calendar-day fa-3x mb-3 text-muted"></i>
                    <p>No daily visitors found</p>
                  </div>
                ) : (
                  <>
                    <div className="table-responsive">
                      <table className="table table-hover">
                        <thead>
                          <tr>
                            <th>Photo</th>
                            <th>Name</th>
                            <th>Type</th>
                            <th>Contact</th>
                            <th>Units</th>
                            <th>Today&apos;s Status</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {dailyVisitors.map(visitor => (
                            <tr key={visitor.id}>
                              <td>
                                {visitor.photo_url ? (
                                  <img
                                    src={getImageUrl(visitor.photo_url)}
                                    alt={visitor.name}
                                    className="rounded-circle"
                                    style={{ width: '40px', height: '40px', objectFit: 'cover' }}
                                    onError={(e) => {
                                      e.target.onerror = null;
                                      e.target.src = '/no-image.svg';
                                    }}
                                  />
                                ) : (
                                  <div
                                    className="rounded-circle bg-secondary d-flex align-items-center justify-content-center text-white"
                                    style={{ width: '40px', height: '40px' }}
                                  >
                                    <i className="fas fa-user"></i>
                                  </div>
                                )}
                              </td>
                              <td>{visitor.name}</td>
                              <td>{visitor.type || '-'}</td>
                              <td>{visitor.contact || '-'}</td>
                              <td>{getVisitorUnitsLabel(visitor)}</td>
                              <td>{getDailyVisitorStatusBadge(visitor)}</td>
                              <td>
                                <div className="btn-group">
                                  {/* Show Check-In button when can_check_in is true (not checked in OR already checked out) */}
                                  {(visitor.can_check_in || visitor.current_status === 'not_checked_in' || visitor.current_status === 'checked_out') && visitor.current_status !== 'checked_in' && (
                                    <button
                                      className="btn btn-sm btn-success me-1"
                                      onClick={() => checkInDailyVisitor(visitor)}
                                      disabled={loading}
                                      title="Check In"
                                    >
                                      <i className="fas fa-sign-in-alt"></i>
                                    </button>
                                  )}
                                  {/* Show Check-Out button only when currently checked in */}
                                  {visitor.current_status === 'checked_in' && (
                                    <button
                                      className="btn btn-sm btn-danger me-1"
                                      onClick={() => checkOutDailyVisitor(visitor)}
                                      disabled={loading}
                                      title="Check Out"
                                    >
                                      <i className="fas fa-sign-out-alt"></i>
                                    </button>
                                  )}
                                  <button
                                    className="btn btn-sm btn-outline-primary me-1"
                                    onClick={() => openEditDailyVisitorModal(visitor)}
                                    title="Edit"
                                  >
                                    <i className="fas fa-edit"></i>
                                  </button>
                                  <button
                                    className="btn btn-sm btn-outline-danger"
                                    onClick={() => deleteDailyVisitor(visitor)}
                                    title="Delete"
                                  >
                                    <i className="fas fa-trash"></i>
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Pagination */}
                    {dailyVisitorsPagination.totalPages > 1 && (
                      <div className="d-flex justify-content-center mt-3">
                        <nav aria-label="Daily visitors pagination">
                          <ul className="pagination pagination-sm">
                            <li className={`page-item ${dailyVisitorsPagination.currentPage === 1 ? 'disabled' : ''}`}>
                              <button
                                className="page-link"
                                onClick={() => handleDailyVisitorsPageChange(dailyVisitorsPagination.currentPage - 1)}
                                disabled={dailyVisitorsPagination.currentPage === 1}
                              >
                                <i className="fas fa-chevron-left"></i>
                              </button>
                            </li>
                            {Array.from({ length: dailyVisitorsPagination.totalPages }, (_, i) => i + 1).map(pageNum => (
                              <li
                                key={pageNum}
                                className={`page-item ${pageNum === dailyVisitorsPagination.currentPage ? 'active' : ''}`}
                              >
                                <button
                                  className="page-link"
                                  onClick={() => handleDailyVisitorsPageChange(pageNum)}
                                >
                                  {pageNum}
                                </button>
                              </li>
                            ))}
                            <li className={`page-item ${dailyVisitorsPagination.currentPage === dailyVisitorsPagination.totalPages ? 'disabled' : ''}`}>
                              <button
                                className="page-link"
                                onClick={() => handleDailyVisitorsPageChange(dailyVisitorsPagination.currentPage + 1)}
                                disabled={dailyVisitorsPagination.currentPage === dailyVisitorsPagination.totalPages}
                              >
                                <i className="fas fa-chevron-right"></i>
                              </button>
                            </li>
                          </ul>
                        </nav>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Daily Visitor Add/Edit Modal */}
      {showDailyVisitorModal && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="fas fa-calendar-day me-2"></i>
                  {dailyVisitorModalMode === 'add' ? 'Add Daily Visitor' : 'Edit Daily Visitor'}
                </h5>
                <button type="button" className="btn-close" onClick={closeDailyVisitorModal}></button>
              </div>
              <form onSubmit={saveDailyVisitor}>
                <div className="modal-body">
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Name <span className="text-danger">*</span></label>
                      <input
                        type="text"
                        className="form-control"
                        name="name"
                        value={dailyVisitorFormData.name}
                        onChange={handleDailyVisitorInputChange}
                        placeholder="Enter visitor name"
                        required
                      />
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Contact Number</label>
                      <input
                        type="tel"
                        className="form-control"
                        name="contact"
                        value={dailyVisitorFormData.contact}
                        onChange={handleDailyVisitorInputChange}
                        placeholder="Enter contact number"
                      />
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Type</label>
                      <input
                        type="text"
                        className="form-control"
                        name="type"
                        value={dailyVisitorFormData.type}
                        onChange={handleDailyVisitorInputChange}
                        placeholder="Enter visitor type"
                      />
                    </div>
                  </div>

                  {/* Villas / Units multi-select */}
                  <div className="row">
                    <div className="col-md-12 mb-3">
                      <label className="form-label">
                        Select Villas / Units <span className="text-muted">(multiple allowed)</span>
                      </label>
                      <select
                        className="form-select"
                        multiple
                        value={dailyVisitorFormData.unit_ids}
                        onChange={(e) => {
                          const options = Array.from(e.target.selectedOptions);
                          const values = options.map(opt => opt.value);
                          setDailyVisitorFormData(prev => ({
                            ...prev,
                            unit_ids: values
                          }));
                        }}
                      >
                        {localVillas && localVillas.length > 0 ? (
                          localVillas.map(villa => (
                            <option key={villa.id} value={villa.id}>
                              {villa.villa_number || `Unit ${villa.id}`}
                            </option>
                          ))
                        ) : (
                          <option value="" disabled>
                            No units available
                          </option>
                        )}
                      </select>
                      <small className="text-muted">
                        Hold Ctrl (Windows) or Cmd (Mac) to select multiple villas.
                      </small>
                    </div>
                  </div>

                  <div className="row">
                    {/* Photo Section */}
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Visitor Photo</label>
                      <div className="d-flex gap-2 mb-2">
                        <button
                          type="button"
                          className="btn btn-outline-primary btn-sm"
                          onClick={startDailyVisitorCamera}
                        >
                          <i className="fas fa-camera me-1"></i>
                          Capture Photo
                        </button>
                        <label className="btn btn-outline-secondary btn-sm mb-0">
                          <i className="fas fa-upload me-1"></i>
                          Upload
                          <input
                            type="file"
                            accept="image/*"
                            className="d-none"
                            onChange={(e) => {
                              const file = e.target.files[0];
                              if (file) {
                                setDailyVisitorPhotoFile(file);
                                // For preview, use a temporary object URL
                                const previewUrl = URL.createObjectURL(file);
                                setDailyVisitorCapturedPhoto(previewUrl);
                                setDailyVisitorPhotoRemoved(false);
                              }
                            }}
                          />
                        </label>
                      </div>

                      {showDailyVisitorCamera && (
                        <div className="card mb-2">
                          <div className="card-body p-2">
                            <video
                              ref={dailyVisitorVideoRef}
                              width="100%"
                              autoPlay
                              playsInline
                              muted
                              style={{ maxHeight: '200px' }}
                            ></video>
                            <canvas ref={dailyVisitorCanvasRef} style={{ display: 'none' }}></canvas>
                            <div className="text-center mt-2">
                              <button
                                type="button"
                                className="btn btn-primary btn-sm me-2"
                                onClick={captureDailyVisitorPhoto}
                              >
                                <i className="fas fa-camera me-1"></i>
                                Capture
                              </button>
                              <button
                                type="button"
                                className="btn btn-secondary btn-sm"
                                onClick={stopDailyVisitorCamera}
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        </div>
                      )}

                      {dailyVisitorCapturedPhoto && (
                        <div className="position-relative d-inline-block">
                          <img
                            src={
                              dailyVisitorCapturedPhoto.startsWith('data:') ||
                              dailyVisitorCapturedPhoto.startsWith('blob:')
                                ? dailyVisitorCapturedPhoto
                                : getImageUrl(dailyVisitorCapturedPhoto)
                            }
                            alt="Captured photo"
                            className="img-thumbnail"
                            style={{ maxHeight: '150px' }}
                          />
                          <button
                            type="button"
                            className="btn btn-sm btn-danger position-absolute top-0 end-0"
                            onClick={() => {
                              setDailyVisitorCapturedPhoto(null);
                              setDailyVisitorPhotoFile(null);
                              // Mark as removed if we're editing and had an existing photo
                              if (dailyVisitorModalMode === 'edit' && editingDailyVisitor?.photo_url) {
                                setDailyVisitorPhotoRemoved(true);
                              }
                            }}
                          >
                            <i className="fas fa-times"></i>
                          </button>
                        </div>
                      )}
                    </div>

                    {/* ID Document Section */}
                    <div className="col-md-6 mb-3">
                      <label className="form-label">ID Document</label>
                      <div className="d-flex gap-2 mb-2">
                        <button
                          type="button"
                          className="btn btn-outline-primary btn-sm"
                          onClick={startDailyVisitorIdCamera}
                        >
                          <i className="fas fa-camera me-1"></i>
                          Capture ID
                        </button>
                        <label className="btn btn-outline-secondary btn-sm mb-0">
                          <i className="fas fa-upload me-1"></i>
                          Upload
                          <input
                            type="file"
                            accept="image/*"
                            className="d-none"
                            onChange={(e) => {
                              const file = e.target.files[0];
                              if (file) {
                                setDailyVisitorIdDocFile(file);
                                const previewUrl = URL.createObjectURL(file);
                                setDailyVisitorCapturedIdDoc(previewUrl);
                                setDailyVisitorIdDocRemoved(false);
                              }
                            }}
                          />
                        </label>
                      </div>

                      {showDailyVisitorIdCamera && (
                        <div className="card mb-2">
                          <div className="card-body p-2">
                            <video
                              ref={dailyVisitorIdVideoRef}
                              width="100%"
                              autoPlay
                              playsInline
                              muted
                              style={{ maxHeight: '200px' }}
                            ></video>
                            <canvas ref={dailyVisitorIdCanvasRef} style={{ display: 'none' }}></canvas>
                            <div className="text-center mt-2">
                              <button
                                type="button"
                                className="btn btn-primary btn-sm me-2"
                                onClick={captureDailyVisitorIdDoc}
                              >
                                <i className="fas fa-camera me-1"></i>
                                Capture
                              </button>
                              <button
                                type="button"
                                className="btn btn-secondary btn-sm"
                                onClick={stopDailyVisitorIdCamera}
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        </div>
                      )}

                      {dailyVisitorCapturedIdDoc && (
                        <div className="position-relative d-inline-block">
                          <img
                            src={
                              dailyVisitorCapturedIdDoc.startsWith('data:') ||
                              dailyVisitorCapturedIdDoc.startsWith('blob:')
                                ? dailyVisitorCapturedIdDoc
                                : getImageUrl(dailyVisitorCapturedIdDoc)
                            }
                            alt="ID Document"
                            className="img-thumbnail"
                            style={{ maxHeight: '150px' }}
                          />
                          <button
                            type="button"
                            className="btn btn-sm btn-danger position-absolute top-0 end-0"
                            onClick={() => {
                              setDailyVisitorCapturedIdDoc(null);
                              setDailyVisitorIdDocFile(null);
                              // Mark as removed if we're editing and had an existing ID document
                              if (dailyVisitorModalMode === 'edit' && editingDailyVisitor?.document_id_url) {
                                setDailyVisitorIdDocRemoved(true);
                              }
                            }}
                          >
                            <i className="fas fa-times"></i>
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={closeDailyVisitorModal}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={loading}>
                    {loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                        Saving...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-save me-1"></i>
                        {dailyVisitorModalMode === 'add' ? 'Add Visitor' : 'Save Changes'}
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Upcoming Visitors Tab */}
      {activeTab === 'upcoming' && (
        <div className="row">
          <div className="col-md-12">
            <div className="card">
              <div className="card-header d-flex justify-content-between align-items-center">
                <h5 className="card-title mb-0">Upcoming Visitors</h5>
                <span className="badge bg-info">{getUpcomingVisitors().length}</span>
              </div>
              <div className="card-body">
                {getUpcomingVisitors().length === 0 ? (
                  <div className="text-center py-4">
                    <i className="fas fa-clock fa-3x mb-3 text-muted"></i>
                    <p>No upcoming visitors at the moment</p>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-hover">
                      <thead>
                        <tr>
                          <th>ID</th>
                          <th>Visitor Name</th>
                          <th>Unit</th>
                          <th>Purpose</th>
                          <th>Expected Date</th>
                          <th>Expected Time</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {getUpcomingVisitors().map(visitor => (
                          <tr key={visitor.id}>
                            <td>{visitor.id}</td>
                            <td>{visitor.visitor_name}</td>
                            <td>{visitor.villa_number}</td>
                            <td>{visitor.purpose}</td>
                            <td>{visitor.expected_date || 'Not specified'}</td>
                            <td>{visitor.expected_time || 'Not specified'}</td>
                            <td>
                              <div className="btn-group">
                                <button
                                  className="btn btn-sm btn-info me-1"
                                  onClick={() => handleShowDetails(visitor.id)}
                                >
                                  <i className="fas fa-eye"></i>
                                </button>
                                <button
                                  className="btn btn-sm btn-success"
                                  onClick={() => {
                                    setVisitorToCheckIn(visitor);
                                    setShowCheckInModal(true);
                                  }}
                                >
                                  <i className="fas fa-sign-in-alt"></i> Check-In
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Requested Visitors Tab */}
      {activeTab === 'requested' && (
        <div className="row">
          <div className="col-md-12">
            <div className="card">
              <div className="card-header d-flex justify-content-between align-items-center">
                <h5 className="card-title mb-0">Requested Visitors</h5>
                <span className="badge bg-warning">{requestedVisitorsPagination.total}</span>
              </div>
              <div className="card-body">
                {requestedVisitorsPagination.total === 0 ? (
                  <div className="text-center py-4">
                    <i className="fas fa-user-clock fa-3x mb-3 text-muted"></i>
                    <p>No visitors waiting for owner approval</p>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-hover">
                      <thead>
                        <tr>
                          <th>ID</th>
                          <th>Visitor Name</th>
                          <th>Unit</th>
                          <th>Purpose</th>
                          <th>Expected Date</th>
                          <th>Expected Time</th>
                          <th>Requested By</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {getRequestedVisitors().map(visitor => (
                          <tr key={visitor.id}>
                            <td>{visitor.id}</td>
                            <td>{visitor.visitor_name}</td>
                            <td>{visitor.villa_number}</td>
                            <td>{visitor.purpose}</td>
                            <td>{visitor.expected_date || 'Not specified'}</td>
                            <td>{visitor.expected_time || 'Not specified'}</td>
                            <td>{visitor.created_by_name || 'Owner'}</td>
                            <td>
                              <div className="btn-group">
                                <button
                                  className="btn btn-sm btn-info me-1"
                                  onClick={() => handleShowDetails(visitor.id)}
                                >
                                  <i className="fas fa-eye"></i>
                                </button>
                                <button
                                  className="btn btn-sm btn-success"
                                  onClick={() => {
                                    setVisitorToCheckIn(visitor);
                                    setShowCheckInModal(true);
                                  }}
                                  disabled={visitor.status !== 'approved'}
                                  title={visitor.status !== 'approved' ? 'Waiting for owner approval' : 'Check-in visitor'}
                                >
                                  <i className="fas fa-sign-in-alt"></i> Check-In
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Pagination for Requested Visitors */}
                {requestedVisitorsPagination.totalPages > 1 && (
                  <div className="d-flex justify-content-center mt-3">
                    <nav aria-label="Requested visitors pagination">
                      <ul className="pagination pagination-sm">
                        {/* Previous Page */}
                        <li className={`page-item ${requestedVisitorsPagination.currentPage === 1 ? 'disabled' : ''}`}>
                          <button
                            className="page-link"
                            onClick={() => handleRequestedVisitorsPageChange(requestedVisitorsPagination.currentPage - 1)}
                            disabled={requestedVisitorsPagination.currentPage === 1}
                          >
                            <i className="fas fa-chevron-left"></i>
                          </button>
                        </li>

                        {/* Page Numbers */}
                        {Array.from({ length: requestedVisitorsPagination.totalPages }, (_, i) => i + 1).map(pageNum => {
                          // Show first page, last page, current page, and pages around current page
                          const shouldShow =
                            pageNum === 1 ||
                            pageNum === requestedVisitorsPagination.totalPages ||
                            Math.abs(pageNum - requestedVisitorsPagination.currentPage) <= 1;

                          if (!shouldShow) {
                            // Show ellipsis if there's a gap
                            if (pageNum === 2 && requestedVisitorsPagination.currentPage > 3) {
                              return (
                                <li key={`ellipsis-start`} className="page-item disabled">
                                  <span className="page-link">...</span>
                                </li>
                              );
                            }
                            if (pageNum === requestedVisitorsPagination.totalPages - 1 && requestedVisitorsPagination.currentPage < requestedVisitorsPagination.totalPages - 2) {
                              return (
                                <li key={`ellipsis-end`} className="page-item disabled">
                                  <span className="page-link">...</span>
                                </li>
                              );
                            }
                            return null;
                          }

                          return (
                            <li key={pageNum} className={`page-item ${pageNum === requestedVisitorsPagination.currentPage ? 'active' : ''}`}>
                              <button
                                className="page-link"
                                onClick={() => handleRequestedVisitorsPageChange(pageNum)}
                              >
                                {pageNum}
                              </button>
                            </li>
                          );
                        })}

                        {/* Next Page */}
                        <li className={`page-item ${requestedVisitorsPagination.currentPage === requestedVisitorsPagination.totalPages ? 'disabled' : ''}`}>
                          <button
                            className="page-link"
                            onClick={() => handleRequestedVisitorsPageChange(requestedVisitorsPagination.currentPage + 1)}
                            disabled={requestedVisitorsPagination.currentPage === requestedVisitorsPagination.totalPages}
                          >
                            <i className="fas fa-chevron-right"></i>
                          </button>
                        </li>
                      </ul>
                    </nav>
                  </div>
                )}

                {/* Pagination Info */}
                {requestedVisitorsPagination.total > 0 && (
                  <div className="text-center text-muted mt-2">
                    <small>
                      Showing {((requestedVisitorsPagination.currentPage - 1) * requestedVisitorsPagination.limit) + 1} to{' '}
                      {Math.min(requestedVisitorsPagination.currentPage * requestedVisitorsPagination.limit, requestedVisitorsPagination.total)} of{' '}
                      {requestedVisitorsPagination.total} requested visitors
                    </small>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Visitor Details Modal */}
      {showDetailsModal && selectedVisitor && (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.6)' }}>
          <div className="modal-dialog modal-lg modal-dialog-centered" style={{ maxWidth: '850px' }}>
            <div className="modal-content border-0 shadow">
              <div className="modal-header" style={{ backgroundColor: settings.block_title_bg_color || '#f8f9fa', color: settings.block_title_color || '#8B4513' }}>
                <h5 className="modal-title">
                  <i className="fas fa-user-circle me-2"></i>
                  Visitor Details: {selectedVisitor.visitor_name}
                  <div className="mt-1" style={{ fontSize: '0.8em' }}>
                    {getStatusBadge(selectedVisitor.status)}
                    {selectedVisitor.status === 'pending_approval' && (
                      <small className="ms-2 text-warning">
                        <i className="fas fa-clock me-1"></i>
                        Awaiting owner approval
                      </small>
                    )}
                  </div>
                </h5>
                <button type="button" className="btn-close" onClick={handleCloseDetails}
                  style={{ backgroundImage: closeIconUrl, fontSize: '18px', opacity: '0.9' }}
                ></button>
              </div>
              <div className="modal-body p-3">
                <div className="row g-0">
                  {/* Left Side - Visitor Information */}
                  <div className="col-md-7 p-3 border-end">
                    <div className="visitor-info mb-3">
                      <h5 className="border-bottom pb-2 mb-2">{selectedVisitor.visitor_name} <span className={`ms-2 badge ${selectedVisitor.status === 'active' ? 'bg-success' : selectedVisitor.status === 'completed' ? 'bg-secondary' : 'bg-primary'}`}>{selectedVisitor.status}</span></h5>

                      <div className="d-flex justify-content-between border-bottom py-2">
                        <strong>Phone:</strong>
                        <span>{selectedVisitor.phone || 'Not provided'}</span>
                      </div>

                      <div className="d-flex justify-content-between border-bottom py-2">
                        <strong>Unit:</strong>
                        <span>{selectedVisitor.villa_number}</span>
                      </div>

                      <div className="d-flex justify-content-between border-bottom py-2">
                        <strong>Purpose:</strong>
                        <span>{selectedVisitor.purpose}</span>
                      </div>
                    </div>

                    <div className="visit-details">
                      <h6 className="mb-2 text-muted">Visit Timeline</h6>

                      <div className="d-flex justify-content-between border-bottom py-2">
                        <strong>Check-In Time:</strong>
                        <span className="text-success">{selectedVisitor.time_in ? formatVisitorDateTime(selectedVisitor.time_in) : 'Not checked in'}</span>
                      </div>

                      <div className="d-flex justify-content-between border-bottom py-2">
                        <strong>Check-Out Time:</strong>
                        <span className={selectedVisitor.time_out ? 'text-success' : 'text-danger'}>
                          {selectedVisitor.time_out ? formatVisitorDateTime(selectedVisitor.time_out) : 'Not checked out'}
                        </span>
                      </div>

                      <div className="d-flex justify-content-between border-bottom py-2">
                        <strong>Duration:</strong>
                        <span>{selectedVisitor.time_in ? calculateDuration(selectedVisitor.time_in, selectedVisitor.time_out) : 'Not applicable'}</span>
                      </div>

                      <div className="d-flex justify-content-between py-2">
                        <strong>Expected Duration:</strong>
                        <span>{selectedVisitor.expected_duration ? formatTimeDuration(selectedVisitor.expected_duration) : 'Not specified'}</span>
                      </div>
                    </div>
                  </div>

                  {/* Right Side - Photos */}
                  <div className="col-md-5 bg-light p-3">
                    <h6 className="text-center mb-2 text-muted">Visitor Verification</h6>

                    {/* Visitor Photo */}
                    <div className="mb-3">
                      <p className="small text-center text-muted mb-1">Visitor Photo</p>
                      <div className="d-flex justify-content-center">
                        <div className="img-container d-flex justify-content-center align-items-center border bg-white rounded" style={{ height: '160px', width: '100%' }}>
                          {/* Replace the problematic img tag with improved version */}
                          <img
                            src={getSafeImageUrl(
                              selectedVisitor.photo_url ||
                              directVisitorImageSrc ||
                              getImageFromLocalStorage(selectedVisitor.id, 'photo')
                            )}
                            alt="Visitor"
                            className="img-fluid"
                            style={{ maxHeight: '145px', maxWidth: '100%', objectFit: 'contain' }}
                            onError={(e) => handleImageLoadError(e, 'No Photo', visitorImageRef)}
                            crossOrigin="anonymous"
                            ref={visitorImageRef}
                          />
                        </div>
                      </div>
                    </div>

                    {/* ID Document Photo */}
                    <div>
                      <p className="small text-center text-muted mb-1">ID Document</p>
                      <div className="d-flex justify-content-center">
                        <div className="img-container d-flex justify-content-center align-items-center border bg-white rounded" style={{ height: '160px', width: '100%' }}>
                          {/* Replace the problematic img tag with improved version */}
                          <img
                            src={getSafeImageUrl(
                              selectedVisitor.id_document_url ||
                              directIdDocumentImageSrc ||
                              getImageFromLocalStorage(selectedVisitor.id, 'id_document')
                            )}
                            alt="ID Document"
                            className="img-fluid"
                            style={{ maxHeight: '145px', maxWidth: '100%', objectFit: 'contain' }}
                            onError={(e) => handleImageLoadError(e, 'No ID Document', idDocumentImageRef)}
                            crossOrigin="anonymous"
                            ref={idDocumentImageRef}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer bg-light">
                <div className="w-100 d-flex justify-content-between">
                  <button type="button" className="btn btn-outline-secondary" onClick={handleCloseDetails}>
                    <i className="fas fa-times me-2"></i>
                    Close
                  </button>

                  {selectedVisitor.status === 'active' && !selectedVisitor.time_out && (
                    <button
                      type="button"
                      className="btn btn-danger"
                      onClick={() => {
                        // Execute checkout directly with visitor ID
                        handleCheckoutVisitor(selectedVisitor.id.toString());
                        handleCloseDetails();
                      }}
                    >
                      <i className="fas fa-sign-out-alt me-2"></i>
                      Check Out
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Visitors;